import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  User,
  Student,
  ClassRosterStudent,
  ParentSummary,
  ParentLinkResult,
  ParentRelationship,
  CoparentSummary,
  UpcomingBooking,
  Location,
  LoginCredentials,
  FormDefinition,
  FormResponse,
  FormStatus,
  FormType,
  FormOwnerLevel,
  FormSection,
  FormInvitation,
  FormDistribution,
  Message,
  MessageDelivery,
  MessageType,
  MessageTargetType,
  MessageDeliveryStatus,
  Thread,
  ThreadMessage,
  ThreadStatus,
  Observation,
  ObservationLearningGoal,
  ObservationDelivery,
  ObservationPhoto,
  ObservationVisibility,
  SessionObservationSummary,
  Moment,
  FeedItem,
  CreateMomentInput,
  ChildMomentSummary,
  ParentIncident,
  IncidentDelivery,
  StaffIncident,
  CreateIncidentInput,
  IncidentDetailResponse,
  IncidentRegisterFilters,
  IncidentAmendment,
  IncidentSeverity,
  NotifiableCategory,
  IncidentFieldsConfig,
  FireDrill,
  FireDrillType,
  FireDrillScope,
  FireDrillIssueCategory,
  FireDrillEditEntry,
  FireDrillCadence,
  CleaningArea,
  CleaningCompletion,
  CleaningFrequency,
  CleaningAreaWithStatus,
  LearningGoal,
  LearningGoalFramework,
  LearningGoalCoverage,
  MedicationRequirement,
  MedicationRequirementWithEntries,
  MedicationRequirementType,
  MedicationRoute,
  MedicationSchedule,
  MedicationEntryType,
  MedicationAdministrationEntry,
  ParentSafeMedication,
  MedicationDueSlot,
  SessionSummary,
  Policy,
  PolicyAudience,
  PolicyVersion,
  PolicyReviewRecord,
  PolicyRegisterRow,
  PolicyCoverage,
  PolicySignatures,
  PolicyChecklistKey,
  ParentAction,
  ParentSafePolicy,
  StaffPolicyRow,
  StudentPolicyAcceptance,
  EnrollmentHistoryResponse,
  MedicationEmergencyBadge,
  UnifiedCalendarResponse,
  Closure,
  ClosureImpact,
  ClosureScope,
  ClosureStatus,
  ChecklistKind,
  ChecklistAnswerValue,
  ChecklistItem,
  ClientChecklistRun,
  WritableVettingStatus,
  CredentialType,
  ClientVettingRecord,
  ClientCredentialRecord,
  ClientReferenceCheck,
  StaffComplianceProfile,
  StaffComplianceRegisterEntry,
  StaffExpectedItemsConfig,
} from '../../types';
import type {
  RolloverClassPreview,
  RolloverPreviewBody,
  RolloverApplyBody,
  RolloverApplyResult,
  RolloverUndoResult,
} from '../../types/rollover';
import type {
  SessionShape,
  LiveSessionShape,
  AttendanceEventShape,
  PickupContactShape,
  PickupContactGroupShape,
  StudentDayStateShape,
  ReleasedToInput,
  AttendanceRegisterResponse,
  AttendanceConcernsResponse,
  StudentAttendanceHistoryResponse,
} from '../../types/attendance-v2';
import type {
  ResolvedRetentionRule,
  RetentionRecordType,
  RetentionOverviewRow,
  StudentLegalHold,
} from '../../types/retention';
import { getBaseUrl } from '../../config/api';
import { logout, tokenRefreshed } from '../slices/authSlice';

/**
 * Build the `X-Active-Context` header value from the user's currently selected
 * membership. Format mirrors what the backend's `parseActiveContextHeader`
 * helper expects:
 *   loc:<locationId>:<role>   for location-scoped roles
 *   org:<organizationId>:<role> for org-level roles (ORG_ADMIN)
 *   org::<role>               for SUPER_ADMIN (no specific org/location)
 *
 * Returns null when the user isn't loaded yet or the active membership is
 * malformed — in that case we omit the header and the backend falls back to
 * the user's first active membership.
 */
function buildActiveContextHeader(authState: any): string | null {
  const user = authState?.user;
  if (!user) return null;
  const idx = authState.activeMembershipIndex ?? 0;
  const membership = user.memberships?.[idx];
  if (!membership?.role) return null;

  if (membership.role === 'SUPER_ADMIN') {
    return `org::SUPER_ADMIN`;
  }
  if (membership.locationId) {
    return `loc:${membership.locationId}:${membership.role}`;
  }
  if (membership.organizationId) {
    return `org:${membership.organizationId}:${membership.role}`;
  }
  return null;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const authState = (getState() as any).auth;
    const token = authState?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const activeContext = buildActiveContextHeader(authState);
    if (activeContext) {
      headers.set('X-Active-Context', activeContext);
    }
    return headers;
  },
});

/**
 * Endpoints where a 401 means "wrong credentials" rather than "your session
 * is dead" — the user is mid-authentication, so we surface the error inline
 * instead of refreshing or booting them.
 */
export const AUTH_ENTRY_PATHS = [
  '/auth/login',
  '/auth/social-login',
  '/auth/social-callback',
  '/auth/token/refresh',
  '/auth/mfa/challenge',
  // MFA enrollment re-verifies the CURRENT password server-side. A wrong
  // password there returns 401 meaning "that password is wrong" — NOT "your
  // session expired". Treat these like auth-entry endpoints so the 401 passes
  // through to the panel as an inline error instead of triggering the
  // refresh-and-logout flow (which would sign the user out mid-enrollment).
  '/auth/mfa/setup/start',
  '/auth/mfa/setup/verify',
  // Change Password re-verifies the CURRENT password server-side. A wrong
  // current password returns 401 meaning "that password is wrong" — NOT "your
  // session expired". Without this, the global 401 interceptor clears tokens
  // and boots the user to /login on a mistyped current password. Same class of
  // bug as the MFA-setup entries above.
  '/auth/change-password',
  // Push registration is best-effort and fires for ANY authenticated session,
  // including a user with zero active memberships (whose session is valid but
  // whom the backend 401s here). That 401 means "not a push-eligible session",
  // NOT "your session is dead" — treat it as auth-entry so it never triggers
  // the refresh-and-logout flow, which would bounce a zero-membership user off
  // the NoActiveMembership "finish your invite" screen. See PushSync.tsx.
  '/notifications/subscribe',
  '/notifications/unsubscribe',
];

// Single in-flight refresh promise shared across concurrent 401s. Without
// this, every parallel query that gets a 401 would fire its own refresh,
// rotate the Cognito refresh token, and invalidate the others.
let refreshInFlight: Promise<string | null> | null = null;

const attemptRefresh = async (api: Parameters<BaseQueryFn>[1]): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;

  const state = (api.getState() as any).auth;
  const currentRefreshToken: string | null = state?.refreshToken ?? null;
  if (!currentRefreshToken) return null;

  refreshInFlight = (async () => {
    const refreshResult = await rawBaseQuery(
      {
        url: '/auth/token/refresh',
        method: 'POST',
        body: { refreshToken: currentRefreshToken },
      },
      api,
      {}
    );

    if (refreshResult.error) return null;
    const data = refreshResult.data as
      | { accessToken?: string; refreshToken?: string | null }
      | undefined;
    if (!data?.accessToken) return null;

    api.dispatch(
      tokenRefreshed({
        token: data.accessToken,
        refreshToken: data.refreshToken ?? null,
      })
    );
    return data.accessToken;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

/**
 * Wraps fetchBaseQuery with refresh-and-retry on 401.
 *
 *   1. First call returns 401 from a non-auth-entry endpoint
 *   2. We try POST /auth/token-refresh with the stored refresh token
 *   3. On success, we swap in the new access token and retry the original request once
 *   4. On failure (no refresh token, or refresh returns an error), log out
 *
 * Concurrent 401s share the same in-flight refresh promise so we don't
 * burn through Cognito's refresh-token rotation.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) return result;

  const url = typeof args === 'string' ? args : args.url;
  const isAuthEntryEndpoint = AUTH_ENTRY_PATHS.some((p) => url.includes(p));
  if (isAuthEntryEndpoint) return result;

  const newAccessToken = await attemptRefresh(api);
  if (!newAccessToken) {
    api.dispatch(logout());
    api.dispatch(localApi.util.resetApiState());
    return result;
  }

  // Retry once with the fresh token (prepareHeaders re-reads from state).
  result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    // Refresh succeeded but the original request still 401s — token isn't
    // the problem (likely permission/role). Don't loop.
    api.dispatch(logout());
    api.dispatch(localApi.util.resetApiState());
  }
  return result;
};

/**
 * Successful login result: real tokens + the loaded user profile.
 */
export type LoginTokens = {
  user: User;
  token: string;
  accessToken: string;
  refreshToken: string | null;
};

/**
 * Login result when the account has TOTP MFA enrolled: Cognito issued a
 * SOFTWARE_TOKEN_MFA challenge instead of tokens. No `email` — the login
 * form the caller just submitted already has it (see user-login handler for
 * why the backend won't echo it back).
 */
export type MfaChallenge = {
  mfaRequired: true;
  challenge: 'SOFTWARE_TOKEN_MFA';
  session: string;
};

export type LoginResult = LoginTokens | MfaChallenge;

/**
 * Anything that changes WHO is expected in a class on a given day, or WHETHER a
 * class runs that day, also changes today's sessions, the "Run today's session"
 * button, and the calendar — none of which are re-derivable from the Class or
 * Student caches alone. Enrollment/roster/term/class-lifecycle mutations must
 * invalidate these too, or the client keeps serving stale session data until a
 * hard reload (impossible in the installed PWA). Spread this into their
 * `invalidatesTags`. (The session-attendance mutations already invalidate
 * Session/MeToday directly.)
 */
const SESSION_AFFECTING_TAGS = ['Session', 'MeToday', 'Calendar'] as const;

/**
 * The filename the server put on a download. Falls back rather than throwing:
 * a missing or unparsable header must still produce a usable download, not a
 * failed one.
 */
const filenameFromContentDisposition = (header: string | null): string => {
  const match = header?.match(/filename="([^"]+)"/);
  return match?.[1] || 'policy-signatures.csv';
};

/**
 * Local API for development with docker-compose backend
 */
export const localApi = createApi({
  reducerPath: 'localApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Location',
    'Student',
    'Term',
    'Context',
    'Form',
    'FormResponse',
    'FormInvitation',
    'StudentIntakes',
    'Class',
    'ClassInstance',
    'Organization',
    'Team',
    'ParentOnboarding',
    // Session-based attendance
    'Session',
    'AttendanceEvent',
    'PickupContact',
    'MyPickupContacts',
    'MeToday',
    'PlannedAbsence',
    // Messaging
    'Message',
    'MessageDelivery',
    'MessageUnreadCount',
    'Thread',
    // Notifications
    'NotificationPrefs',
    // Observations
    'Observation',
    'ObservationDelivery',
    'ObservationUnreadCount',
    // Moments (photo moments + parent feed)
    'Moment',
    'SessionSummary',
    // Incidents (parent-facing)
    'Incident',
    'IncidentFieldsConfig',
    // Fire Drills
    'FireDrill',
    'FireDrillCadence',
    // Fees
    'Fee',
    // Cleaning Schedules
    'CleaningArea',
    'CleaningCompletion',
    // Learning Goals (Aistéar)
    'LearningGoal',
    // Medication
    'Medication',
    'MedicationsDue',
    // Policy vault
    'PolicyRegister',
    'Policy',
    'MyPolicies',
    'PolicyCoverage',
    'StudentPolicies',
    // Unified Calendar & Closures
    'Calendar',
    'Closure',
    // Daily Checklists
    'ChecklistItem',
    'ChecklistRun',
    'ChecklistQueue',
    // Staff Compliance (Garda Vetting & Credentials)
    'StaffCompliance',
    // Retention (schedule + overview; clocks only)
    'RetentionSchedule',
    'RetentionOverview',
    // TOTP MFA enrollment state (Settings → Security)
    'MfaStatus',
  ],
  endpoints: (builder) => ({
    getPolicyVersionFile: builder.query<
      { url: string; expiresIn: number },
      { policyId: string; versionId: string; variant?: 'child-friendly' }
    >({
      query: ({ policyId, versionId, variant }) => ({
        url: `/policies/${policyId}/versions/${versionId}/file`,
        params: variant ? { variant } : undefined,
      }),
      keepUnusedDataFor: 0,
    }),
    // Auth endpoints
    //
    // Login can return either real tokens or (for a TOTP-enrolled account) an
    // `mfaRequired` challenge — Cognito issued a SOFTWARE_TOKEN_MFA challenge
    // instead of tokens. The caller narrows the union with `'mfaRequired' in
    // result` before treating it as LoginTokens (see Login.tsx).
    login: builder.mutation<LoginResult, LoginCredentials>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      transformResponse: (
        response: { user: User; accessToken: string; refreshToken?: string | null } | MfaChallenge
      ): LoginResult => {
        if ('mfaRequired' in response) {
          // Pass through unchanged — there's no accessToken to read yet, and
          // the backend deliberately omits `email` here (see user-login
          // handler) to avoid widening user enumeration.
          return response;
        }
        return {
          user: { ...response.user, memberships: (response.user as any).memberships || [] },
          token: response.accessToken,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken ?? null,
        };
      },
    }),

    // Step 2 of MFA login: exchange the challenge session + a 6-digit TOTP
    // code for real tokens. Unauthenticated endpoint (no bearer token yet) —
    // listed in AUTH_ENTRY_PATHS so a 401 (MFA_CODE_INVALID) surfaces inline
    // instead of triggering the refresh-and-logout flow.
    mfaChallenge: builder.mutation<LoginTokens, { email: string; code: string; session: string }>({
      query: (body) => ({
        url: '/auth/mfa/challenge',
        method: 'POST',
        body,
      }),
      transformResponse: (response: {
        user: User;
        accessToken: string;
        refreshToken?: string | null;
      }): LoginTokens => ({
        user: { ...response.user, memberships: (response.user as any).memberships || [] },
        token: response.accessToken,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken ?? null,
      }),
    }),

    // MFA enrollment (settings panel, Task 8). All three re-verify the
    // caller's current password server-side before touching Cognito's MFA
    // state — no refresh token is ever sent in these bodies.
    // setup/start only issues a secret — it does NOT change enabled state, so
    // it invalidates nothing. (The password 401 here surfaces inline because
    // /auth/mfa/setup is an AUTH_ENTRY_PATH, not a token-expiry that logs out.)
    mfaSetupStart: builder.mutation<{ secret: string; otpauthUri: string }, { password: string }>({
      query: (body) => ({ url: '/auth/mfa/setup/start', method: 'POST', body }),
    }),
    // On successful enrollment, invalidate MfaStatus so the panel badge flips
    // to "Enabled" without a page reload.
    mfaSetupVerify: builder.mutation<{ success: true }, { password: string; code: string }>({
      query: (body) => ({ url: '/auth/mfa/setup/verify', method: 'POST', body }),
      invalidatesTags: ['MfaStatus'],
    }),
    mfaStatus: builder.query<{ enabled: boolean }, void>({
      query: () => ({ url: '/auth/mfa/status', method: 'GET' }),
      providesTags: ['MfaStatus'],
    }),
    mfaDisable: builder.mutation<{ success: true }, void>({
      query: () => ({ url: '/auth/mfa/disable', method: 'POST' }),
      invalidatesTags: ['MfaStatus'],
    }),

    // Admin-initiated MFA reset (Task 8) — tenant-scoped in the path, same
    // shape as the other org/location member-management mutations. The
    // backend re-derives the target's rank from their loaded memberships, so
    // this call is just the request; the authz guard lives server-side.
    mfaAdminReset: builder.mutation<
      { success: true },
      { organizationId: string; locationId: string; targetUserId: string }
    >({
      query: ({ organizationId, locationId, targetUserId }) => ({
        url: `/organizations/${organizationId}/locations/${locationId}/mfa-reset`,
        method: 'POST',
        body: { targetUserId },
      }),
    }),

    // Password reset — both flows share /auth/password-reset; the backend
    // selects the action by inspecting the request body shape.
    requestPasswordReset: builder.mutation<{ message: string }, { email: string }>({
      query: (body) => ({ url: '/auth/password-reset', method: 'POST', body }),
    }),
    confirmPasswordReset: builder.mutation<
      { message: string },
      { email: string; code: string; newPassword: string }
    >({
      query: (body) => ({ url: '/auth/password-reset', method: 'POST', body }),
    }),

    // Self-service password change for the signed-in user. The access token is
    // attached by prepareHeaders (Authorization: Bearer …); the backend reads it
    // from there and validates the current password against Cognito.
    changePassword: builder.mutation<
      { success: boolean; message: string },
      { previousPassword: string; proposedPassword: string }
    >({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', body }),
    }),

    // Dashboard stats
    getDashboardStats: builder.query<
      {
        locationId: string;
        role: string;
        totalStudents: number;
        myChildren: number;
        totalFamilies: number;
        totalLocations: number;
        totalClasses: number;
        totalStaff: number;
        totalOrganizations: number;
        openIncidents?: number;
        notifiableOverdue?: boolean;
        // Informational "leaving in the next 60 days" list (spec §3). Present
        // for STAFF/LOCATION_ADMIN (one location) and ORG_ADMIN (org-wide).
        leavingSoon?: Array<{ id: string; name: string; expectedEndDate: string }>;
      },
      { locationId?: string; role?: string; organizationId?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.locationId) queryParams.append('locationId', params.locationId);
        if (params.role) queryParams.append('role', params.role);
        if (params.organizationId) queryParams.append('organizationId', params.organizationId);
        return `/dashboard/stats?${queryParams.toString()}`;
      },
    }),

    // Session endpoints
    getContexts: builder.query<
      {
        availableContexts: Array<{
          role: string;
          locationId?: string;
          locationName?: string;
          organizationId?: string;
          organizationName?: string;
        }>;
        groupedContexts: { organizations: any[]; locations: any[] };
        totalContexts: number;
      },
      void
    >({
      query: () => '/session/contexts',
      providesTags: ['Context'],
    }),

    switchContext: builder.mutation<
      { message: string; activeContext: any },
      { role: string; locationId?: string; organizationId?: string }
    >({
      query: (body) => ({
        url: '/session/context/switch',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'Context',
        'Student',
        'Form',
        'FormResponse',
        'FormInvitation',
        'Class',
        'ClassInstance',
        'Organization',
        'Location',
        'Team',
      ],
    }),

    // Planned absences (separate entity from AttendanceEvent — filed in
    // advance, no session required). Invalidates the per-day rollups so
    // the calendar and Today strip refresh.
    reportPlannedAbsence: builder.mutation<
      any,
      {
        studentId: string;
        classId: string;
        date: string;
        notes?: string;
      }
    >({
      query: (body) => ({
        url: '/attendance/planned-absence',
        method: 'POST',
        body,
      }),
      // 'Calendar' added alongside the parent calendar (Task 22): the
      // unified calendar endpoint carries PLANNED_ABSENT per class entry
      // (see derivePerClassDayStatus), so without this tag a freshly-filed
      // absence never appears on the calendar grid until an unrelated
      // refetch happens to occur.
      invalidatesTags: ['PlannedAbsence', 'Session', 'MeToday', 'Calendar'],
    }),

    // Soft-cancel a filed planned absence (Task 24/26). Server rejects a
    // cancel for today-or-past dates and 404s cleanly on a double-cancel —
    // both are handled in the caller. Same invalidation set as
    // reportPlannedAbsence: a cancelled absence must disappear from the
    // calendar grid, not just this mutation's own tag.
    cancelPlannedAbsence: builder.mutation<any, { absenceId: string }>({
      query: ({ absenceId }) => ({
        url: `/attendance/planned-absence/${absenceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PlannedAbsence', 'Session', 'MeToday', 'Calendar'],
    }),

    // ============== Attendance v2 — sessions, events, pickup, today ==============

    // Lazy-create or fetch the session for a given class+date. Returns
    // { session, events } so the staff page renders the full live state
    // in one query.
    getSessionForClassDate: builder.query<
      {
        session: SessionShape;
        events: AttendanceEventShape[];
        /** Students with an active PlannedAbsence for the session's class+date,
         *  evaluated at request time. Live (not snapshotted like
         *  session.expectedStudentIds) so parent-filed absences after start
         *  reach the staff roster (Bug 11b). */
        absentStudentIds: string[];
      },
      { classId: string; date: string }
    >({
      query: ({ classId, date }) => `/sessions?classId=${classId}&date=${date}`,
      providesTags: (result) =>
        result?.session
          ? [
              { type: 'Session' as const, id: result.session.id },
              { type: 'AttendanceEvent' as const, id: result.session.id },
            ]
          : ['Session', 'AttendanceEvent'],
    }),

    // Read a session by id (re-fetches after mutations).
    getSession: builder.query<
      {
        session: SessionShape;
        events: AttendanceEventShape[];
        /** Students with an active PlannedAbsence for the session's class+date,
         *  evaluated at request time. Live (not snapshotted like
         *  session.expectedStudentIds) so parent-filed absences after start
         *  reach the staff roster (Bug 11b). */
        absentStudentIds: string[];
        /** The session's organization, derived server-side from its location.
         *  Used by the in-session incident modal (org-scoped): a SUPER_ADMIN's
         *  active membership carries no org, so the incident belongs to the
         *  session's org, not the operator's. */
        organizationId?: string;
      },
      string
    >({
      query: (sessionId) => `/sessions/${sessionId}`,
      providesTags: (_result, _err, sessionId) => [
        { type: 'Session', id: sessionId },
        { type: 'AttendanceEvent', id: sessionId },
      ],
    }),

    // List all sessions at a location on a date (staff "Today at location" view).
    listSessionsForLocation: builder.query<
      { sessions: SessionShape[] },
      { locationId: string; date: string }
    >({
      query: ({ locationId, date }) => `/sessions?locationId=${locationId}&date=${date}`,
      providesTags: ['Session'],
    }),

    // The "what's running right now" board for the dashboard. Returns today's
    // IN_PROGRESS sessions, scoped by role server-side: admins see every live
    // session at the location, plain staff only the ones for classes they're
    // assigned to. Lets anyone covering the floor walk straight back into a
    // session (sessions are multi-staff — join, not resume).
    listLiveSessions: builder.query<{ sessions: LiveSessionShape[] }, { locationId: string }>({
      query: ({ locationId }) => `/sessions/live?locationId=${locationId}`,
      providesTags: ['Session'],
    }),

    // Real session rows for a class over a date range, so the Classes page can
    // label each upcoming instance by its LIVE status (Scheduled/In-progress/
    // Ended/Cancelled) — the class-instances feed is schedule-only and can't
    // tell a finished session from one not yet started.
    getClassSessions: builder.query<
      {
        sessions: Array<{
          id: string;
          date: string;
          status: string;
          scheduledStart: string;
          scheduledEnd: string;
        }>;
      },
      { classId: string; from: string; to: string }
    >({
      query: ({ classId, from, to }) => `/classes/${classId}/sessions?from=${from}&to=${to}`,
      providesTags: ['Session'],
    }),

    startSession: builder.mutation<{ session: SessionShape }, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/sessions/${sessionId}/start`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'Session', id: sessionId },
        { type: 'AttendanceEvent', id: sessionId },
        'MeToday',
      ],
    }),

    endSession: builder.mutation<
      { session: SessionShape; rolledForward: number; closedOut: number },
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: `/sessions/${sessionId}/end`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'Session', id: sessionId },
        { type: 'AttendanceEvent', id: sessionId },
        'Session',
        'AttendanceEvent',
        'MeToday',
      ],
    }),

    sessionCheckIn: builder.mutation<
      { event: AttendanceEventShape },
      { sessionId: string; studentId: string; at?: string; notes?: string }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/sessions/${sessionId}/check-in`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'AttendanceEvent', id: sessionId },
        'MeToday',
      ],
    }),

    sessionCheckOut: builder.mutation<
      { event: AttendanceEventShape },
      {
        sessionId: string;
        studentId: string;
        releasedTo: ReleasedToInput;
        pickupPhotoS3Key?: string;
        at?: string;
        notes?: string;
      }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/sessions/${sessionId}/check-out`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'AttendanceEvent', id: sessionId },
        'MeToday',
      ],
    }),

    sessionMultiCheckOut: builder.mutation<
      { events: AttendanceEventShape[]; count: number },
      {
        sessionId: string;
        studentIds: string[];
        releasedTo: ReleasedToInput;
        pickupPhotoS3Key?: string;
        at?: string;
      }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/sessions/${sessionId}/multi-check-out`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'AttendanceEvent', id: sessionId },
        'MeToday',
      ],
    }),

    sessionVoidCheckIn: builder.mutation<
      { event: AttendanceEventShape },
      { sessionId: string; studentId: string; reason: string }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/sessions/${sessionId}/void-event`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: 'AttendanceEvent', id: sessionId },
        'MeToday',
      ],
    }),

    // Pickup contacts — list (everyone with student access), add/remove (parent + admin).
    listPickupContacts: builder.query<{ contacts: PickupContactShape[] }, string>({
      query: (studentId) => `/students/${studentId}/pickup-contacts`,
      providesTags: (_result, _err, studentId) => [{ type: 'PickupContact', id: studentId }],
    }),

    // Parent account-level pickup contact management (enter once, assign to multiple kids).
    listMyPickupContacts: builder.query<{ groups: PickupContactGroupShape[] }, void>({
      query: () => '/parents/me/pickup-contacts',
      providesTags: ['MyPickupContacts'],
    }),

    addMyPickupContact: builder.mutation<
      { group: PickupContactGroupShape },
      {
        name: string;
        relationship: string;
        relationshipType: string;
        phone?: string;
        studentIds: string[];
      }
    >({
      query: (body) => ({ url: '/parents/me/pickup-contacts', method: 'POST', body }),
      invalidatesTags: ['MyPickupContacts', 'PickupContact'],
    }),

    updateMyPickupContact: builder.mutation<
      { group: PickupContactGroupShape },
      {
        groupId: string;
        name: string;
        relationship: string;
        relationshipType: string;
        phone?: string;
        studentIds: string[];
        isGuardian?: boolean;
      }
    >({
      query: ({ groupId, ...body }) => ({
        url: `/parents/me/pickup-contacts/${groupId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['MyPickupContacts', 'PickupContact'],
    }),

    deleteMyPickupContact: builder.mutation<void, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/parents/me/pickup-contacts/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MyPickupContacts', 'PickupContact'],
    }),

    // Parent Today strip rollup.
    getMeToday: builder.query<{ today: StudentDayStateShape[] }, void>({
      query: () => '/me/today',
      providesTags: ['MeToday'],
    }),

    // Unified calendar — ONE role-aware endpoint replacing the old
    // /calendar/student and /calendar/class routes (both deleted). The slice
    // of classes returned is derived server-side from the caller's loaded
    // active role, never from these query params:
    //   - PARENT: studentId is REQUIRED (the handler 400s without it).
    //   - STAFF: locationId/classId only narrow the caller's assigned classes.
    //   - LOCATION_ADMIN/ORG_ADMIN/SUPER_ADMIN: locationId is REQUIRED.
    getCalendar: builder.query<
      UnifiedCalendarResponse,
      { from: string; to: string; locationId?: string; classId?: string; studentId?: string }
    >({
      query: ({ from, to, locationId, classId, studentId }) => {
        const params = new URLSearchParams({ from, to });
        if (locationId) params.set('locationId', locationId);
        if (classId) params.set('classId', classId);
        if (studentId) params.set('studentId', studentId);
        return `/calendar?${params.toString()}`;
      },
      providesTags: ['Calendar'],
    }),

    // ============== Attendance register (Tusla) ==============
    // Read-only derivations over sessions/events/absences — nothing in the app
    // mutates them directly, so no cache tags. The attendance that feeds them
    // is recorded elsewhere (a tablet's check-in never touches this cache), so
    // AttendancePage passes `refetchOnMountOrArgChange: true` and every visit
    // reads them fresh. Admin-only server-side: staff/parents get a 403, and
    // the pages self-gate on isAdmin.

    // Range is hard-capped at 31 days server-side (400 beyond it).
    getAttendanceRegister: builder.query<
      AttendanceRegisterResponse,
      { locationId: string; from: string; to: string; classId?: string }
    >({
      query: ({ locationId, from, to, classId }) => {
        const params = new URLSearchParams({ from, to });
        if (classId) params.set('classId', classId);
        return `/locations/${locationId}/attendance-register?${params.toString()}`;
      },
    }),

    getAttendanceConcerns: builder.query<
      AttendanceConcernsResponse,
      { locationId: string; windowDays?: number }
    >({
      query: ({ locationId, windowDays }) => {
        const params = new URLSearchParams();
        if (windowDays != null) params.set('windowDays', String(windowDays));
        const q = params.toString();
        return `/locations/${locationId}/attendance-concerns${q ? `?${q}` : ''}`;
      },
    }),

    getStudentAttendanceHistory: builder.query<
      StudentAttendanceHistoryResponse,
      { studentId: string; from?: string; to?: string }
    >({
      query: ({ studentId, from, to }) => {
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const q = params.toString();
        return `/students/${studentId}/attendance-history${q ? `?${q}` : ''}`;
      },
    }),

    // ============== Closures (location/class-level closed periods) ==============

    // GET /locations/{locationId}/closures?from&to — open to any in-tenant
    // member (STAFF and PARENT included), not just admins.
    listClosures: builder.query<
      { closures: Closure[] },
      { locationId: string; from: string; to: string }
    >({
      query: ({ locationId, from, to }) =>
        `/locations/${locationId}/closures?from=${from}&to=${to}`,
      providesTags: ['Closure'],
    }),

    // POST /locations/{locationId}/closures/impact — read-only preview shown
    // before an admin confirms a closure. Writes nothing, so no tag
    // invalidation.
    closureImpact: builder.mutation<
      { impact: ClosureImpact },
      {
        locationId: string;
        startDate: string;
        endDate: string;
        scope: ClosureScope;
        classIds?: string[];
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/closures/impact`,
        method: 'POST',
        body,
      }),
    }),

    // POST /locations/{locationId}/closures — declares a closure. `notify`
    // controls whether affected families are fanned a push notification
    // AFTER the closure commits. Invalidates both Closure and Calendar: a
    // new closure changes what the calendar grid shows.
    createClosure: builder.mutation<
      { closure: Closure },
      {
        locationId: string;
        startDate: string;
        endDate: string;
        label: string;
        note?: string;
        scope: ClosureScope;
        classIds?: string[];
        status?: ClosureStatus;
        notify: boolean;
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/closures`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Closure', 'Calendar'],
    }),

    // PATCH /closures/{closureId} — partial patch; only keys present in the
    // body are forwarded (an omitted field is left untouched server-side).
    updateClosure: builder.mutation<
      { closure: Closure },
      {
        closureId: string;
        startDate?: string;
        endDate?: string;
        label?: string;
        note?: string;
        scope?: ClosureScope;
        classIds?: string[];
        status?: ClosureStatus;
      }
    >({
      query: ({ closureId, ...body }) => ({
        url: `/closures/${closureId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Closure', 'Calendar'],
    }),

    // DELETE /closures/{closureId}
    deleteClosure: builder.mutation<void, { closureId: string }>({
      query: ({ closureId }) => ({
        url: `/closures/${closureId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Closure', 'Calendar'],
    }),

    // Class endpoints
    getClasses: builder.query<
      Array<{
        id: string;
        name: string;
        locationId: string;
        description?: string;
        startTime: string;
        endTime: string;
        daysOfWeek: number[];
        capacity: number;
        isActive: boolean;
      }>,
      string
    >({
      query: (locationId) => `/classes?locationId=${locationId}`,
      transformResponse: (response: any) => response.data || response,
      providesTags: ['Class'],
    }),

    /**
     * Per-day capacity snapshot for a class. Drives the day-picker's "X / Y
     * remaining" hints and disables full days. Re-fetches when enrollments
     * change anywhere (Class tag invalidation).
     */
    getClassCapacity: builder.query<
      {
        classId: string;
        capacity: number;
        runningDays: number[];
        perDay: { day: number; enrolled: number; remaining: number }[];
      },
      string
    >({
      query: (classId) => `/classes/${classId}/capacity`,
      providesTags: (_result, _err, classId) => [{ type: 'Class', id: `capacity-${classId}` }],
    }),

    getClassTerms: builder.query<
      Array<{
        termId: string;
        classId: string;
        name: string;
        startDate: string;
        endDate: string;
      }>,
      string
    >({
      query: (classId) => `/classes/${classId}/terms`,
      transformResponse: (response: any) => response?.data ?? [],
      providesTags: (_result, _err, classId) => [{ type: 'Term', id: classId }],
    }),

    createTerm: builder.mutation<
      any,
      { classId: string; name: string; startDate: string; endDate: string }
    >({
      query: ({ classId, ...body }) => ({
        url: `/classes/${classId}/terms`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { classId }) => [
        ...SESSION_AFFECTING_TAGS,
        { type: 'Term', id: classId },
        'Class',
      ],
    }),

    updateTerm: builder.mutation<
      any,
      { classId: string; termId: string; startDate?: string; endDate?: string }
    >({
      query: ({ classId, termId, ...body }) => ({
        url: `/classes/${classId}/terms/${termId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { classId }) => [
        ...SESSION_AFFECTING_TAGS,
        { type: 'Term', id: classId },
        'Class',
      ],
    }),

    deleteTerm: builder.mutation<any, { classId: string; termId: string }>({
      query: ({ classId, termId }) => ({
        url: `/classes/${classId}/terms/${termId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { classId }) => [
        ...SESSION_AFFECTING_TAGS,
        { type: 'Term', id: classId },
        'Class',
      ],
    }),

    /**
     * `termId` is optional — omitted, the server books the term covering today.
     * Supplied, it books that term, which may be in the future.
     */
    endEnrollment: builder.mutation<
      any,
      { classId: string; studentId: string; termId?: string; endDate?: string }
    >({
      query: ({ classId, ...body }) => ({
        url: `/classes/${classId}/students/end-enrollment`,
        method: 'POST',
        body: { classId, ...body },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    cancelBooking: builder.mutation<any, { classId: string; studentId: string; termId: string }>({
      query: ({ classId, ...body }) => ({
        url: `/classes/${classId}/students/cancel-booking`,
        method: 'POST',
        body: { classId, ...body },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    transferStudent: builder.mutation<
      any,
      {
        fromClassId: string;
        toClassId: string;
        studentId: string;
        daysOfWeek?: number[];
        lastDay?: string;
      }
    >({
      query: ({ fromClassId, ...body }) => ({
        url: `/classes/${fromClassId}/students/transfer`,
        method: 'POST',
        body: { fromClassId, ...body },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    // ── September rollover (Task 20 wizard) ──────────────────────────────────
    // Preview is read-only: no tag invalidation, no cache providesTags — the
    // wizard drives it as a mutation so each recompute (range/leaver edits) is
    // an explicit call rather than a memoised query.
    rolloverPreview: builder.mutation<
      RolloverClassPreview[],
      { locationId: string } & RolloverPreviewBody
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/rollover/preview`,
        method: 'POST',
        body,
      }),
      // The handler returns { success, data: { classes: [...] } }. Unwrap to the
      // bare per-class array the wizard renders.
      transformResponse: (response: any) => response?.data?.classes ?? response?.classes ?? [],
    }),

    // Apply mutates terms + enrolments across the location → invalidate broadly.
    rolloverApply: builder.mutation<
      RolloverApplyResult,
      { locationId: string } & RolloverApplyBody
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/rollover/apply`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => response.data ?? response,
      invalidatesTags: ['Class', 'Student', 'Term', ...SESSION_AFFECTING_TAGS],
    }),

    // Undo deletes the run's created terms + enrolments; same broad invalidation.
    // A 409 (touched terms) surfaces as an error whose `data.blockers` the
    // wizard reads — RTK keeps the raw error body, so no transform is needed.
    rolloverUndo: builder.mutation<RolloverUndoResult, { locationId: string; rolloverId: string }>({
      query: ({ locationId, rolloverId }) => ({
        url: `/locations/${locationId}/rollover/${rolloverId}/undo`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: any) => response.data ?? response,
      invalidatesTags: ['Class', 'Student', 'Term', ...SESSION_AFFECTING_TAGS],
    }),

    createClass: builder.mutation<
      any,
      {
        locationId: string;
        name: string;
        startTime: string;
        endTime: string;
        daysOfWeek: number[];
        description?: string;
        room?: string;
        capacity: number;
        // §5.1: a class is created WITH its first term — required.
        term: { name: string; startDate: string; endDate: string };
      }
    >({
      query: (body) => ({ url: '/classes', method: 'POST', body }),
      invalidatesTags: ['Class', 'Term', ...SESSION_AFFECTING_TAGS],
    }),

    updateClass: builder.mutation<any, { classId: string; updates: Record<string, any> }>({
      query: ({ classId, updates }) => ({
        url: `/classes/${classId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Class', ...SESSION_AFFECTING_TAGS],
    }),

    /**
     * Close a class (§5.2). `lastDay` sets the current term's new end (default
     * today server-side); `notifyParents` opts into a parent notification;
     * `hardDelete` erases a never-used class. Replaces the old bare deactivate.
     * A 409 body carries `affectedChildren` / `blockingTerms` for the UI to show.
     */
    closeClass: builder.mutation<
      any,
      { classId: string; lastDay?: string; notifyParents?: boolean; hardDelete?: boolean }
    >({
      query: ({ classId, ...body }) => ({
        url: `/classes/${classId}`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Class', 'Term', ...SESSION_AFFECTING_TAGS],
    }),

    // Computed instances (generated from schedule + overrides)
    getClassInstances: builder.query<
      Array<{
        classId: string;
        className: string;
        date: string;
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        status: string;
        reason?: string;
        isOverridden: boolean;
      }>,
      { classId: string; startDate: string; endDate: string }
    >({
      query: ({ classId, startDate, endDate }) =>
        `/classes/${classId}/instances?startDate=${startDate}&endDate=${endDate}`,
      transformResponse: (response: any) => response.data || response,
      providesTags: (result, error, { classId }) => [{ type: 'ClassInstance', id: classId }],
    }),

    // Cancel or modify a single class day
    setClassOverride: builder.mutation<
      any,
      {
        classId: string;
        date: string;
        status: 'CANCELLED' | 'MODIFIED';
        reason?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: ({ classId, date, ...body }) => ({
        url: `/classes/${classId}/instances/${date}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { classId }) => [
        ...SESSION_AFFECTING_TAGS,
        { type: 'ClassInstance', id: classId },
      ],
    }),

    // Restore default schedule for a day
    removeClassOverride: builder.mutation<any, { classId: string; date: string }>({
      query: ({ classId, date }) => ({
        url: `/classes/${classId}/instances/${date}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { classId }) => [
        ...SESSION_AFFECTING_TAGS,
        { type: 'ClassInstance', id: classId },
      ],
    }),

    // Enrollment endpoints
    getClassStudents: builder.query<
      {
        classId: string;
        students: ClassRosterStudent[];
        count: number;
        /**
         * Bookings for terms that have not started. Kept separate from
         * `students` — they are not on today's roster, but must be visible so
         * they can be cancelled.
         */
        upcoming: UpcomingBooking[];
      },
      string
    >({
      query: (classId) => `/classes/${classId}/students`,
      transformResponse: (response: any) => ({
        classId: response.classId || '',
        students: response.data || response.students || [],
        count: response.count || (response.data || response.students || []).length,
        upcoming: response.upcoming || [],
      }),
      providesTags: (result, error, classId) => [{ type: 'Student', id: `class-${classId}` }],
    }),

    enrollStudent: builder.mutation<
      any,
      {
        classId: string;
        studentId: string;
        daysOfWeek: number[];
        termId?: string;
        /** YYYY-MM-DD the enrolment starts; omitted, the server picks the earliest allowed. */
        effectiveFrom?: string;
      }
    >({
      query: ({ classId, studentId, daysOfWeek, termId, effectiveFrom }) => ({
        url: `/classes/${classId}/students`,
        method: 'POST',
        // termId omitted entirely when absent, so the server keeps its
        // resolve-the-covering-term default.
        body: {
          studentId,
          daysOfWeek,
          ...(termId ? { termId } : {}),
          ...(effectiveFrom ? { effectiveFrom } : {}),
        },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    /** Change the days a child attends this class (the roster's Edit days). */
    updateEnrollmentDays: builder.mutation<
      {
        classId: string;
        termId: string;
        studentId: string;
        daysOfWeek: number[];
        effectiveFrom: string;
      },
      { classId: string; studentId: string; daysOfWeek: number[]; effectiveFrom?: string }
    >({
      query: ({ classId, studentId, daysOfWeek, effectiveFrom }) => ({
        url: `/classes/${classId}/students/${studentId}`,
        method: 'PATCH',
        body: { daysOfWeek, ...(effectiveFrom ? { effectiveFrom } : {}) },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    unenrollStudent: builder.mutation<any, { classId: string; studentId: string }>({
      query: ({ classId, studentId }) => ({
        url: `/classes/${classId}/students/${studentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    getStudentClasses: builder.query<
      {
        studentId: string;
        /**
         * `daysOfWeek` is the CLASS's running schedule, not the child's
         * per-enrolment subset — the handler returns Class rows
         * (`getEnrolledClassesForStudentOnDate`), and an enrolment's days are
         * always a subset of them. Callers wanting "which days does this kid
         * actually attend?" must not treat this as the answer; it is only an
         * upper bound. Declared here because the response already carries it
         * (widening the type, not the payload).
         */
        classes: Array<{
          id: string;
          name: string;
          locationId: string;
          daysOfWeek?: number[];
        }>;
        count: number;
      },
      string
    >({
      query: (studentId) => `/students/${studentId}/classes`,
      transformResponse: (response: any) => ({
        studentId: response.studentId || '',
        classes: response.data || response.classes || [],
        count: response.count || (response.data || response.classes || []).length,
      }),
    }),

    // Staff-only (`student:read`). Tagged 'Student' so every enrolment
    // mutation (which invalidates the bare 'Student' tag) refreshes it, and
    // 'Class' so a class or term rename (bare 'Class') refreshes its names.
    getEnrollmentHistory: builder.query<EnrollmentHistoryResponse, string>({
      query: (studentId) => `/students/${studentId}/enrollment-history`,
      providesTags: (result, error, studentId) => [
        { type: 'Student', id: `enrollment-history-${studentId}` },
        { type: 'Class', id: `enrollment-history-${studentId}` },
      ],
    }),

    // Enriched students (with classes + parent info) for table view
    getEnrichedStudents: builder.query<
      {
        students: Array<
          Student & {
            /** `startsOn`: a class the child starts later; `daysOfWeek` are the days they will have. */
            classes: Array<{ id: string; name: string; daysOfWeek: number[]; startsOn?: string }>;
            parentName?: string;
            parentEmail?: string;
          }
        >;
        count: number;
      },
      { locationId?: string; includeArchived?: boolean } | void
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params?.locationId) qp.append('locationId', params.locationId);
        // Reveal ARCHIVED (LEFT >12mo) children. Default view hides them.
        if (params?.includeArchived) qp.append('includeArchived', 'true');
        const qs = qp.toString();
        return `/students/enriched${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Student'],
    }),

    // Bulk enroll students in a class. Each entry carries the per-student
    // days; the backend rejects the whole batch if any (student × day) would
    // breach capacity.
    bulkEnrollStudents: builder.mutation<
      {
        message: string;
        enrolled: number;
        failed: number;
        // A refused From date carries the code; a too-early one its `earliest`.
        // ALREADY_ENROLLED with a scheduled last day carries `until` and `message`.
        errors?: {
          studentId: string;
          error: string;
          earliest?: string;
          until?: string;
          message?: string;
        }[];
        // Children who held an ENDED enrolment in this term and were reopened
        // (on the requested days) rather than newly written.
        rejoined?: { studentId: string; daysOfWeek: number[]; effectiveFrom?: string }[];
        enrolledStudents?: { studentId: string; effectiveFrom: string }[];
      },
      {
        classId: string;
        students: Array<{ studentId: string; daysOfWeek: number[]; effectiveFrom?: string }>;
      }
    >({
      query: ({ classId, students }) => ({
        url: `/classes/${classId}/students/bulk`,
        method: 'POST',
        body: { students },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    // Student endpoints
    getStudentsByParent: builder.query<Student[], string>({
      query: (parentId) => `/students?parentId=${parentId}`,
      providesTags: ['Student'],
    }),

    getStudents: builder.query<Student[], string>({
      query: (locationId) => (locationId ? `/students?locationId=${locationId}` : '/students'),
      providesTags: ['Student'],
    }),

    /**
     * Paginated student list with optional search/grade/class filters. Used by
     * the Send-form student picker so a CONSENT distribution at a large
     * location doesn't have to render every student at once. The backend
     * returns `{ students, total, limit, offset }` whenever `limit` is set.
     */
    getStudentsPaginated: builder.query<
      { students: Student[]; total: number; limit: number; offset: number },
      {
        locationId: string;
        search?: string;
        grade?: string;
        classId?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ locationId, search, grade, classId, limit = 25, offset = 0 }) => {
        const params = new URLSearchParams({
          locationId,
          limit: String(limit),
          offset: String(offset),
        });
        if (search) params.set('search', search);
        if (grade) params.set('grade', grade);
        if (classId) params.set('classId', classId);
        return `/students?${params.toString()}`;
      },
      providesTags: ['Student'],
    }),

    // CSV export — returns a Blob so the caller can pipe it to a download link.
    // Going through RTK (rather than raw fetch) means a 401 here triggers the
    // standard refresh-and-retry interceptor.
    exportStudents: builder.mutation<
      Blob,
      {
        locationId?: string;
        organizationId?: string;
        includeInactive?: boolean;
        grade?: string;
        classId?: string;
        search?: string;
      }
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params.locationId) qs.set('locationId', params.locationId);
        else if (params.organizationId) qs.set('organizationId', params.organizationId);
        if (params.includeInactive) qs.set('includeInactive', 'true');
        if (params.grade) qs.set('grade', params.grade);
        if (params.classId) qs.set('classId', params.classId);
        if (params.search) qs.set('search', params.search);
        const q = qs.toString();
        return {
          url: `/students/export${q ? `?${q}` : ''}`,
          method: 'GET',
          responseHandler: (response: Response) => response.blob(),
          cache: 'no-cache',
        };
      },
    }),

    getLocationGrades: builder.query<string[], string>({
      query: (locationId) => `/students/grades?locationId=${locationId}`,
      transformResponse: (response: { grades?: string[] }) => response.grades || [],
      providesTags: ['Student'],
    }),

    /**
     * Create a Student row. Staff supply only first/last (both optional) and
     * an optional class list. Everything else (DOB, grade, dietary, second
     * parent, primary parent details) comes from the parent's REGISTRATION
     * form submission. The auto-trigger fires the form once the row exists.
     */
    createStudent: builder.mutation<
      {
        message: string;
        students: Array<
          Student & {
            classes?: Array<{ id: string; name: string; locationId: string; daysOfWeek: number[] }>;
          }
        >;
        parents: Array<{
          userId: string;
          email: string;
          name: string;
          relationship: string;
          isNew: boolean;
          emailAction: string;
        }>;
        locationId: string;
        locationName: string;
      },
      {
        /** The location the child is being added to (also scopes the
         *  returning-child duplicate search). */
        locationId: string;
        firstName?: string;
        lastName?: string;
        /**
         * Parent is identified by email only. If a user with this email exists
         * the backend reuses them (and ignores anything else we'd send); if not,
         * a placeholder is created and the parent supplies their real name when
         * accepting the invite-signup link.
         */
        parentEmail: string;
        /** YYYY-MM-DD. Feeds the returning-child duplicate search (name + DOB). */
        dateOfBirth?: string;
        /**
         * termId is OPTIONAL, same contract as the single-enrolment endpoint:
         * absent resolves the term covering today, present books into that
         * specific term (including a FUTURE one, for a class between terms).
         * effectiveFrom (YYYY-MM-DD) is the class's first day; absent means the
         * earliest allowed, as on every other enrol path.
         */
        classes?: Array<{
          classId: string;
          daysOfWeek: number[];
          termId?: string;
          effectiveFrom?: string;
        }>;
        /** Optional STUDENT_INTAKE templates to also send at enrollment. */
        intakeFormIds?: string[];
        /**
         * "Create new anyway" — set after the staff member has seen the
         * returning-child prompt and chosen a fresh record over reactivating a
         * matching LEFT child. Bypasses the server-side duplicate search.
         */
        force?: boolean;
      }
    >({
      query: (body) => ({
        url: '/students/enroll',
        method: 'POST',
        body: {
          locationId: body.locationId,
          parents: [{ email: body.parentEmail.trim().toLowerCase() }],
          students: [
            {
              firstName: body.firstName?.trim() || undefined,
              lastName: body.lastName?.trim() || undefined,
              dateOfBirth: body.dateOfBirth?.trim() || undefined,
              classes: body.classes || [],
            },
          ],
          ...(body.intakeFormIds && body.intakeFormIds.length > 0
            ? { intakeFormIds: body.intakeFormIds }
            : {}),
          ...(body.force ? { force: true } : {}),
        },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    listIntakeTemplates: builder.query<
      {
        templates: Array<{
          formId: string;
          name: string;
          description?: string;
          version: number;
          ownerLevel: string;
        }>;
      },
      { locationId: string }
    >({
      query: ({ locationId }) =>
        `/forms/intake-templates?locationId=${encodeURIComponent(locationId)}`,
      providesTags: ['Form'],
    }),

    // A student's STUDENT_INTAKE invitations (any status) for StudentDetail.
    listStudentIntakeInvitations: builder.query<
      {
        invitations: Array<{
          id: string;
          formId: string;
          formTitle: string;
          status: 'PENDING' | 'COMPLETED' | 'EXPIRED';
          createdAt: string;
          completedAt?: string;
          deadline?: string;
          responseId?: string;
        }>;
      },
      { studentId: string }
    >({
      query: ({ studentId }) => `/students/${studentId}/intake-forms`,
      providesTags: (_r, _e, { studentId }) => [{ type: 'StudentIntakes', id: studentId }],
    }),

    // Admin delete-to-resend: removes the (student, form) intake invitation
    // and any response, unblocking a fresh send.
    deleteIntakeForm: builder.mutation<void, { studentId: string; formId: string }>({
      query: ({ studentId, formId }) => ({
        url: `/students/${studentId}/intake-form/${formId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'StudentIntakes', id: studentId }],
    }),

    updateStudent: builder.mutation<Student, { id: string; updates: Partial<Student> }>({
      query: ({ id, updates }) => ({ url: `/students/${id}`, method: 'PUT', body: updates }),
      invalidatesTags: ['Student'],
    }),

    // Single student by id — full record including gp/allergies. Backs the
    // parent-side medical/allergies section; already authorized server-side
    // for PARENT-own-child via the student:read-own loader rule.
    getStudent: builder.query<Student, string>({
      query: (id) => `/students/${id}`,
      providesTags: (result, error, id) => [{ type: 'Student', id }],
    }),

    // Task-14 lifecycle: mark a child as having LEFT the service. Ends every
    // covering enrolment + cancels future bookings server-side, so the class
    // rosters change too — invalidate Class as well as Student.
    markStudentLeft: builder.mutation<
      {
        message: string;
        studentId: string;
        leftAt: string;
        enrollmentsEnded: number;
        futureBookingsCancelled: number;
      },
      { id: string; leftAt?: string; reason?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/students/${id}/mark-left`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    reactivateStudent: builder.mutation<
      { message: string; studentId: string; status: string },
      string
    >({
      query: (id) => ({ url: `/students/${id}/reactivate`, method: 'POST' }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    // Announce (or clear) a child's planned future departure. Passing a
    // YYYY-MM-DD `expectedEndDate` SETS the last expected day; passing null
    // CLEARS a previously-announced departure. Status is untouched — the daily
    // cron flips ACTIVE→LEFT once the date has passed.
    scheduleStudentLeaving: builder.mutation<
      { message: string; student: { id: string; expectedEndDate: string | null } },
      { id: string; expectedEndDate: string | null }
    >({
      query: ({ id, expectedEndDate }) => ({
        url: `/students/${id}/schedule-leaving`,
        method: 'POST',
        body: { expectedEndDate },
      }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    deleteStudent: builder.mutation<
      { message: string; enrollmentsWithdrawn: number; parentsRemoved: number },
      string
    >({
      query: (id) => ({ url: `/students/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Student'],
    }),

    getStudentOnboarding: builder.query<
      {
        stage: 'signup-pending' | 'form-pending' | 'needs-invitation' | 'complete';
        signupInvite: {
          jti: string;
          status: string;
          expiresAt: string;
          lastSentAt: string;
        } | null;
        formInvitation: {
          id: string;
          formId: string;
          formTitle: string;
          status: string;
          sentAt: string;
        } | null;
      },
      string
    >({
      query: (studentId) => `/students/${studentId}/onboarding`,
      providesTags: (_result, _err, id) => [{ type: 'ParentOnboarding', id }],
    }),

    resendStudentOnboarding: builder.mutation<
      {
        message: string;
        signupAction: 'resent' | 'reissued' | 'skipped';
        formInvitationResent: boolean;
        // True when the handler minted a NEW invitation because none was
        // pending. Distinct from `formInvitationResent` (email of an existing
        // invitation) so the toast can say "issued" vs "re-sent".
        formInvitationCreated?: boolean;
      },
      string
    >({
      query: (studentId) => ({
        url: `/students/${studentId}/onboarding/resend`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'ParentOnboarding', id }],
    }),

    // Task-14: bulk mark-left. Absorbs the old bulk-deactivate — same
    // `{ studentIds }` body — but hits the new route and returns the new shape.
    // Ends enrolments + cancels bookings server-side, so invalidate Class too.
    bulkDeactivateStudents: builder.mutation<
      {
        message: string;
        markedLeft: number;
        enrollmentsEnded: number;
        futureBookingsCancelled: number;
      },
      { studentIds: string[]; leftAt?: string; reason?: string }
    >({
      query: (body) => ({ url: '/students/mark-left', method: 'POST', body }),
      invalidatesTags: ['Student', 'Class', ...SESSION_AFFECTING_TAGS],
    }),

    // Profile endpoint
    updateProfile: builder.mutation<
      {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        phone?: string;
        address?: string;
      },
      // An empty string clears a field; omitting the key leaves it alone.
      { firstName?: string; lastName?: string; phone?: string; address?: string }
    >({
      query: (body) => ({ url: '/profile', method: 'PUT', body }),
    }),

    // Organization endpoints
    getOrganizations: builder.query<
      Array<{
        id: string;
        name: string;
        adminEmail: string;
        billingEmail: string;
        isActive: boolean;
        subscriptionStatus: string;
        subscriptionPlan?: string;
        website?: string;
        phone?: string;
        description?: string;
        locationCount?: number;
        createdAt: string;
        updatedAt: string;
      }>,
      void
    >({
      query: () => '/organizations',
      transformResponse: (response: any) => response.data || response,
      providesTags: ['Organization'],
    }),

    updateOrganization: builder.mutation<
      any,
      { organizationId: string; updates: Record<string, any> }
    >({
      query: ({ organizationId, updates }) => ({
        url: `/organizations/${organizationId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Organization'],
    }),

    createOrganization: builder.mutation<
      any,
      {
        name: string;
        billingEmail: string;
        adminEmail: string;
        subscriptionPlan?: string;
        website?: string;
        phone?: string;
        description?: string;
      }
    >({
      query: (body) => ({ url: '/organizations', method: 'POST', body }),
      invalidatesTags: ['Organization'],
    }),

    // Location endpoints. organizationId is required — the backend
    // returns 400 without it. Callers must either pass a real orgId or
    // use `{ skip: !orgId }` so we don't fire a no-op request during
    // the moment between page mount and context resolution.
    getLocations: builder.query<Location[], string>({
      query: (organizationId) => `/locations?organizationId=${organizationId}`,
      transformResponse: (response: any) => response.data || response,
      providesTags: ['Location'],
    }),

    createLocation: builder.mutation<
      any,
      {
        organizationId: string;
        name: string;
        address: string;
        city: string;
        state: string;
        zipCode: string;
        timezone: string;
        phone?: string;
        email?: string;
        capacity?: number;
      }
    >({
      query: (body) => ({ url: '/locations', method: 'POST', body }),
      invalidatesTags: ['Location'],
    }),

    updateLocation: builder.mutation<any, { locationId: string; updates: Record<string, any> }>({
      query: ({ locationId, updates }) => ({
        url: `/locations/${locationId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Location'],
    }),

    getLocationById: builder.query<Location, string>({
      query: (locationId) => `/locations/${locationId}`,
      transformResponse: (response: any) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'Location', id }],
    }),

    // ============== Forms endpoints ==============
    // List forms
    getForms: builder.query<
      { forms: FormDefinition[]; count: number },
      {
        status?: FormStatus;
        formType?: FormType;
        organizationId?: string;
        locationId?: string;
      } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.append('status', params.status);
        if (params?.formType) queryParams.append('formType', params.formType);
        if (params?.organizationId) queryParams.append('organizationId', params.organizationId);
        if (params?.locationId) queryParams.append('locationId', params.locationId);
        const queryString = queryParams.toString();
        return `/forms${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Form'],
    }),

    // Get single form
    getForm: builder.query<{ form: FormDefinition }, string>({
      query: (formId) => `/forms/${formId}`,
      providesTags: (result, error, id) => [{ type: 'Form', id }],
    }),

    // Fetch the canonical "Default Registration Form" sections for a
    // location. Drives the FormBuilder "Reset to default" button.
    getDefaultRegistrationTemplate: builder.query<
      { title: string; sections: FormSection[] },
      { locationId: string }
    >({
      query: ({ locationId }) => `/forms/registration/default-template?locationId=${locationId}`,
    }),

    // Latest submitted REGISTRATION response for a student. Drives the
    // Registration snapshot on StudentDetail. 404 = not yet submitted.
    getStudentRegistrationResponse: builder.query<{ response: FormResponse }, string>({
      query: (studentId) => `/students/${studentId}/registration-response`,
      providesTags: (_r, _e, studentId) => [{ type: 'Student', id: studentId }],
    }),

    // Every parent/guardian linked to a child, with contact details. Staff and
    // admins only — a PARENT caller gets a 403, so callers must skip the query
    // for a parent viewer. Tagged on the Student so anything that invalidates
    // the child (e.g. re-linking a parent) refetches this list.
    listStudentParents: builder.query<{ parents: ParentSummary[] }, string>({
      query: (studentId) => `/students/${studentId}/parents`,
      providesTags: (_r, _e, studentId) => [{ type: 'Student', id: studentId }],
    }),

    // Admin corrects a linked parent's phone/address (contact details missing
    // or wrong) — never email, that's the sign-in identity. Admin-only;
    // STAFF holds student:read, not student:update, so this 403s for them.
    // Invalidates the same Student tag listStudentParents provides on, so
    // the parent list refetches with the correction.
    updateStudentParentContact: builder.mutation<
      Pick<ParentSummary, 'id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'address'>,
      { studentId: string; parentId: string; phone?: string; address?: string }
    >({
      query: ({ studentId, parentId, ...body }) => ({
        url: `/students/${studentId}/parents/${parentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'Student', id: studentId }],
    }),

    // Admin links another parent/guardian to a child by email. The backend
    // decides whether that means a signup invite or linking an existing
    // account; the response's emailAction says which. Per child — link the
    // same person to a sibling by calling this on the sibling.
    addStudentParent: builder.mutation<
      { parent: ParentLinkResult; studentId: string },
      { studentId: string; email: string; relationship: ParentRelationship }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/students/${studentId}/parents`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'Student', id: studentId }],
    }),

    // Admin unlinks a co-parent. Admin-only on the backend too; the primary
    // parent is refused with 409 PRIMARY_PARENT.
    removeStudentParent: builder.mutation<
      { studentId: string; parentId: string; placeholderRemoved: boolean },
      { studentId: string; parentId: string }
    >({
      query: ({ studentId, parentId }) => ({
        url: `/students/${studentId}/parents/${parentId}`,
        method: 'DELETE',
      }),
      // Also refetch on 404: the row is already gone (another admin removed
      // it), so the stale row must leave the screen rather than keep failing.
      invalidatesTags: (_r, e, { studentId }) =>
        !e || (e as { status?: unknown }).status === 404
          ? [{ type: 'Student', id: studentId }]
          : [],
    }),

    // Create form
    createForm: builder.mutation<
      { message: string; form: FormDefinition },
      {
        title: string;
        description?: string;
        formType: FormType;
        ownerLevel?: FormOwnerLevel;
        locationId?: string;
        locationIds?: string[];
        targetRoles: string[];
        sections?: FormSection[];
      }
    >({
      query: (body) => ({
        url: '/forms',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Form'],
    }),

    // Update form
    updateForm: builder.mutation<
      { message: string; form: FormDefinition },
      {
        formId: string;
        updates: {
          title?: string;
          description?: string;
          status?: FormStatus;
          targetRoles?: string[];
          sections?: FormSection[];
          locationIds?: string[];
        };
      }
    >({
      query: ({ formId, updates }) => ({
        url: `/forms/${formId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (result, error, { formId }) => [{ type: 'Form', id: formId }, 'Form'],
    }),

    // Delete (archive) form
    deleteForm: builder.mutation<{ message: string; formId: string }, string>({
      query: (formId) => ({
        url: `/forms/${formId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Form'],
    }),

    // Submit form response
    submitFormResponse: builder.mutation<
      { message: string; response: FormResponse; invitationCompleted?: boolean },
      {
        formId: string;
        studentId: string;
        responses: Record<string, unknown>;
        invitationId?: string;
        files?: Array<{
          fieldId: string;
          fileId: string;
          fileName: string;
          fileType: string;
          fileSize: number;
          s3Key: string;
        }>;
      }
    >({
      query: ({ formId, ...body }) => ({
        url: `/forms/${formId}/responses`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { formId }) => [
        { type: 'FormResponse', id: formId },
        'FormInvitation', // Invalidate invitations when a response is submitted
      ],
    }),

    // Generic presign endpoint (refactor 2026-06-04). Replaced the
    // form-specific getFormUploadUrl mutation. `contextId` is the
    // purpose-specific parent entity (formId for FORM_ATTACHMENT,
    // sessionId for OBSERVATION_PHOTO).
    getUploadUrl: builder.mutation<
      { uploadUrl: string; s3Key: string; expiresAt: string },
      {
        purpose:
          | 'FORM_ATTACHMENT'
          | 'OBSERVATION_PHOTO'
          | 'MOMENT_PHOTO'
          | 'FIRE_DRILL_PHOTO'
          | 'PICKUP_PHOTO'
          | 'INCIDENT_ATTACHMENT'
          | 'STAFF_CREDENTIAL'
          | 'POLICY_DOCUMENT';
        contextId: string;
        mimeType: string;
        sizeBytes: number;
        fileName?: string;
      }
    >({
      query: (body) => ({
        url: '/uploads/presign',
        method: 'POST',
        body,
      }),
    }),

    // ============== Form Invitations endpoints ==============
    // Get pending invitations for current user
    getMyInvitations: builder.query<{ invitations: FormInvitation[]; count: number }, void>({
      query: () => '/forms/invitations',
      providesTags: ['FormInvitation'],
    }),

    // Get invitation count for nav badge
    getInvitationCount: builder.query<{ count: number }, void>({
      query: () => '/forms/invitations/count',
      providesTags: ['FormInvitation'],
    }),

    // Parent: list of children for the kid picker + aggregated dashboard.
    // Decorated with location/org for the subtitle, plus per-kid badges so
    // attention items (pending forms, pending registration) surface
    // directly in the picker without a second round-trip.
    getMyChildren: builder.query<
      {
        children: Array<{
          id: string;
          firstName: string;
          lastName: string;
          displayName: string;
          locationId: string;
          locationName: string;
          organizationId: string;
          organizationName: string;
          registrationStatus: string;
          // Lifecycle status (Task 17). A LEFT child stays in the list so the
          // parent keeps read-only access to their history; the UI renders that
          // child's pages read-only and shows a "has left" banner.
          status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
          grade?: string;
          badges: { pendingForms: number; registrationPending: boolean };
        }>;
      },
      void
    >({
      query: () => '/me/children',
      providesTags: ['ParentOnboarding', 'Student', 'FormInvitation'],
    }),

    // Other parents/guardians on a specific child. Excludes the caller so the
    // UI list is always "besides me", never "me + me". See handler for what
    // fields are exposed (name + email + relationship + flags — no phone).
    getMyChildCoparents: builder.query<{ coparents: CoparentSummary[] }, { childId: string }>({
      query: ({ childId }) => `/me/children/${childId}/coparents`,
      providesTags: (_r, _e, { childId }) => [{ type: 'Student', id: childId }],
    }),

    // A parent links another parent/guardian to their own child. Same
    // backend flow as the admin add, with the emails naming the inviter.
    // Invalidates the child's Student tag so getMyChildCoparents refetches.
    inviteCoparent: builder.mutation<
      { studentId: string; coparent: ParentLinkResult },
      { childId: string; email: string; relationship: ParentRelationship }
    >({
      query: ({ childId, ...body }) => ({
        url: `/me/children/${childId}/coparents`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { childId }) => [{ type: 'Student', id: childId }],
    }),

    // Get distributions for a form
    getFormDistributions: builder.query<FormDistribution[], string>({
      query: (formId) => `/forms/${formId}/distributions`,
      transformResponse: (response: any) => response.data || response.distributions || response,
      providesTags: (result, error, formId) => [{ type: 'Form', id: `distributions-${formId}` }],
    }),

    // Get distributions + aggregate stats for a form (for org-level views)
    getFormDistributionStats: builder.query<
      {
        distributions: FormDistribution[];
        count: number;
        stats: {
          totalSent: number;
          totalCompleted: number;
          totalPending: number;
          byLocation: Record<string, { sent: number; completed: number; pending: number }>;
        };
      },
      string
    >({
      query: (formId) => `/forms/${formId}/distributions`,
      providesTags: (result, error, formId) => [
        { type: 'Form', id: `distribution-stats-${formId}` },
      ],
    }),

    // Distribute form (creates a distribution + invitations)
    distributeForm: builder.mutation<
      {
        message: string;
        distributionId: string;
        created: number;
        skipped: number;
        errors: number;
      },
      {
        formId: string;
        locationId: string;
        targetType: 'CLASS' | 'LOCATION' | 'SPECIFIC_STUDENTS';
        targetId?: string;
        studentIds?: string[];
        // Event name is required for CONSENT distributions (the parent needs to
        // know what they're consenting to) and enforced server-side for that
        // type. STUDENT_INTAKE sends omit it, so it's optional here.
        eventName?: string;
        eventDate?: string;
        eventDescription?: string;
        deadline?: string;
        isBlocking?: boolean;
      }
    >({
      query: ({ formId, ...body }) => ({
        url: `/forms/${formId}/distribute`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FormInvitation', 'Form'],
    }),

    // Update per-location form status
    updateFormLocationStatus: builder.mutation<
      any,
      { formId: string; locationId: string; status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED' }
    >({
      query: ({ formId, locationId, status }) => ({
        url: `/forms/${formId}/location-status`,
        method: 'PUT',
        body: { locationId, status },
      }),
      invalidatesTags: ['Form'],
    }),

    // Close a CONSENT distribution (admin withdraws — cancels pending invitations)
    closeDistribution: builder.mutation<any, { distributionId: string; formId: string }>({
      query: ({ distributionId }) => ({
        url: `/distributions/${distributionId}/close`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { formId }) => [
        { type: 'Form', id: `distributions-${formId}` },
        { type: 'Form', id: `distribution-stats-${formId}` },
        'FormInvitation',
      ],
    }),

    // ============== Team Management endpoints ==============
    // Platform level: super admins
    getSuperAdmins: builder.query<{ members: any[]; count: number }, void>({
      query: () => '/admins',
      providesTags: ['Team'],
    }),

    addSuperAdmin: builder.mutation<any, { email: string }>({
      query: (body) => ({ url: '/admins', method: 'POST', body }),
      invalidatesTags: ['Team'],
    }),

    removeSuperAdmin: builder.mutation<any, { memberId: string; isPendingInvite?: boolean }>({
      query: ({ memberId, isPendingInvite }) => ({
        url: `/admins/${memberId}${isPendingInvite ? '?isPendingInvite=true' : ''}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Team'],
    }),

    // Organization level: members
    getOrgMembers: builder.query<{ members: any[]; count: number }, string>({
      query: (orgId) => `/organizations/${orgId}/members`,
      providesTags: ['Team'],
    }),

    addOrgMember: builder.mutation<any, { orgId: string; email: string; role: string }>({
      query: ({ orgId, ...body }) => ({
        url: `/organizations/${orgId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team'],
    }),

    removeOrgMember: builder.mutation<
      any,
      { orgId: string; memberId: string; role: string; isPendingInvite?: boolean }
    >({
      query: ({ orgId, memberId, role, isPendingInvite }) => ({
        url: `/organizations/${orgId}/members/${memberId}?role=${role}${
          isPendingInvite ? '&isPendingInvite=true' : ''
        }`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Team'],
    }),

    // Location level: members
    getLocationMembers: builder.query<
      { members: any[]; count: number },
      { orgId: string; locationId: string }
    >({
      query: ({ orgId, locationId }) => `/organizations/${orgId}/locations/${locationId}/members`,
      providesTags: ['Team'],
    }),

    // Narrow staff-readable list for the fire-drill modal. Returns just
    // { id, firstName, lastName, role } per staff/admin at the location —
    // no emails, no pending invites, no PII beyond the display name.
    getLocationStaff: builder.query<
      {
        staff: Array<{ id: string; firstName: string; lastName: string; role: string }>;
      },
      { locationId: string }
    >({
      query: ({ locationId }) => `/locations/${locationId}/staff`,
      providesTags: ['Team'],
    }),

    addLocationMember: builder.mutation<
      any,
      { orgId: string; locationId: string; email: string; role: string; classIds?: string[] }
    >({
      query: ({ orgId, locationId, ...body }) => ({
        url: `/organizations/${orgId}/locations/${locationId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team', 'Class'],
    }),

    // Class ↔ staff assignments. Admins use these to manage who's on which class.
    getClassStaff: builder.query<
      {
        staff: Array<{
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          /** false = still assigned but no longer staff/admin at the location.
           *  Absent from responses served by a backend older than this field. */
          isStaffHere?: boolean;
        }>;
      },
      string
    >({
      query: (classId) => `/classes/${classId}/staff`,
      providesTags: (result, err, classId) => [{ type: 'Class', id: `staff-${classId}` }],
    }),

    setClassStaff: builder.mutation<any, { classId: string; userIds: string[] }>({
      query: ({ classId, userIds }) => ({
        url: `/classes/${classId}/staff`,
        method: 'PUT',
        body: { userIds },
      }),
      invalidatesTags: (result, err, { classId }) => [
        { type: 'Class', id: `staff-${classId}` },
        'Team',
      ],
    }),

    /** Returns the classes a given staff member is assigned to. */
    getStaffClasses: builder.query<
      { data: Array<{ id: string; name: string }> },
      { locationId: string; staffId: string }
    >({
      query: ({ locationId, staffId }) => `/classes?locationId=${locationId}&staffId=${staffId}`,
      providesTags: (result, err, { staffId }) => [{ type: 'Class', id: `for-staff-${staffId}` }],
    }),

    updateLocationMemberRole: builder.mutation<
      any,
      { orgId: string; locationId: string; memberId: string; currentRole: string; newRole: string }
    >({
      query: ({ orgId, locationId, memberId, ...body }) => ({
        url: `/organizations/${orgId}/locations/${locationId}/members/${memberId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Team'],
    }),

    removeLocationMember: builder.mutation<
      any,
      {
        orgId: string;
        locationId: string;
        memberId: string;
        role: string;
        isPendingInvite?: boolean;
      }
    >({
      query: ({ orgId, locationId, memberId, role, isPendingInvite }) => ({
        url: `/organizations/${orgId}/locations/${locationId}/members/${memberId}?role=${role}${
          isPendingInvite ? '&isPendingInvite=true' : ''
        }`,
        method: 'DELETE',
      }),
      // A membership change alters who is (still) staff on every class at the
      // location, so the class-staff lists must refetch too.
      invalidatesTags: ['Team', 'Class'],
    }),

    // ============== Invite Signup endpoints (public, no auth) ==============
    getInvitePreview: builder.query<any, string>({
      query: (token) => `/auth/invite-signup/preview?token=${token}`,
    }),

    completeInviteSignup: builder.mutation<
      any,
      { token: string; password: string; firstName: string; lastName: string }
    >({
      query: (body) => ({ url: '/auth/invite-signup', method: 'POST', body }),
    }),

    // ============== Resend Invite endpoints ==============
    resendAdminInvite: builder.mutation<any, string>({
      query: (memberId) => ({ url: `/admins/${memberId}/resend`, method: 'POST' }),
      invalidatesTags: ['Team'],
    }),

    resendOrgMemberInvite: builder.mutation<any, { orgId: string; memberId: string }>({
      query: ({ orgId, memberId }) => ({
        url: `/organizations/${orgId}/members/${memberId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Team'],
    }),

    resendLocationMemberInvite: builder.mutation<
      any,
      { orgId: string; locationId: string; memberId: string }
    >({
      query: ({ orgId, locationId, memberId }) => ({
        url: `/organizations/${orgId}/locations/${locationId}/members/${memberId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Team'],
    }),

    // ============== Messaging endpoints ==============

    /**
     * Send a message. The body is a discriminated union:
     * - ANNOUNCEMENT: targets org/location/class/students with a targetType
     * - PARENT_DM: targets a specific student's parents
     */
    sendMessage: builder.mutation<
      { messageId: string; recipientCount: number },
      | {
          type: MessageType.ANNOUNCEMENT;
          title: string;
          body: string;
          targetType: MessageTargetType;
          targetOrgId?: string;
          targetLocationId?: string;
          targetClassId?: string;
          targetStudentIds?: string[];
          repliesAllowed?: boolean;
        }
      | {
          type: MessageType.PARENT_DM;
          title: string;
          body: string;
          studentId: string;
          repliesAllowed?: boolean;
        }
    >({
      query: (body) => ({ url: '/messages', method: 'POST', body }),
      invalidatesTags: ['Message', 'MessageDelivery', 'MessageUnreadCount'],
    }),

    /** List messages sent by the current user (staff/admin inbox). */
    listSentMessages: builder.query<
      {
        messages: Array<
          Message & {
            stats: {
              recipientCount: number;
              readCount: number;
              pushFailureCount: number;
              replyCount: number;
            };
          }
        >;
        nextCursor: string | null;
      },
      { cursor?: string; limit?: number }
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params.cursor) qs.set('cursor', params.cursor);
        if (params.limit != null) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return `/messages/sent${q ? `?${q}` : ''}`;
      },
      providesTags: ['Message'],
    }),

    /** Full audit for a single sent message — includes per-recipient delivery rows. */
    getMessageAudit: builder.query<
      {
        message: Message;
        deliveries: Array<
          Pick<
            MessageDelivery,
            | 'id'
            | 'recipientUserId'
            | 'recipientRole'
            | 'status'
            | 'readAt'
            | 'pushStatus'
            | 'pushError'
          >
        >;
      },
      string
    >({
      query: (messageId) => `/messages/${messageId}`,
      providesTags: (_result, _err, messageId) => [{ type: 'Message', id: messageId }],
    }),

    /** Withdraw a sent message (removes it from all recipient inboxes). */
    withdrawMessage: builder.mutation<{ withdrawn: true; deliveriesRemoved: number }, string>({
      query: (messageId) => ({ url: `/messages/${messageId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, messageId) => [
        'Message',
        { type: 'Message', id: messageId },
        'MessageDelivery',
      ],
    }),

    /** List the current user's received messages (parent/staff inbox). */
    listMyMessages: builder.query<
      { deliveries: MessageDelivery[]; nextCursor: string | null },
      { status?: MessageDeliveryStatus; cursor?: string; limit?: number }
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params.status) qs.set('status', params.status);
        if (params.cursor) qs.set('cursor', params.cursor);
        if (params.limit != null) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return `/me/messages${q ? `?${q}` : ''}`;
      },
      providesTags: ['MessageDelivery'],
    }),

    /** Unread message count for the current user (nav badge). */
    getMyUnreadCount: builder.query<{ count: number }, void>({
      query: () => '/me/messages/unread-count',
      providesTags: ['MessageUnreadCount'],
    }),

    /** Mark a single inbox item as read. Backend returns 204 No Content. */
    markMessageRead: builder.mutation<void, string>({
      query: (deliveryId) => ({ url: `/me/messages/${deliveryId}/read`, method: 'POST' }),
      invalidatesTags: ['MessageDelivery', 'MessageUnreadCount'],
    }),

    /** Mark all inbox items as read (optionally filtered by MessageType via ?type=). */
    markAllRead: builder.mutation<{ updated: number }, { type?: MessageType } | void>({
      query: (body) => {
        const type = body && 'type' in body ? body.type : undefined;
        return {
          url: `/me/messages/read-all${type ? `?type=${type}` : ''}`,
          method: 'POST',
        };
      },
      invalidatesTags: ['MessageDelivery', 'MessageUnreadCount'],
    }),

    // ============== Two-way threads (parent <-> staff) ==============
    //
    // A thread post fans out MessageDelivery rows into the same inbox and unread
    // badge that announcements use, so every thread mutation must invalidate
    // 'MessageDelivery' + 'MessageUnreadCount' alongside 'Thread' — otherwise a
    // parent sends a message and the nav badge / inbox go stale.

    /**
     * A parent starts a thread about their own child.
     *
     * Anti-spam: at most one OPEN thread per child. A second attempt returns
     * **409** with body `{ error:'conflict', reason:'THREAD_ALREADY_OPEN', threadId }`
     * — RTK Query surfaces that whole body on the rejected result as
     * `err.data` (with `err.status === 409`), so the caller can read `threadId`
     * and route the parent into the existing thread instead of erroring. Do not
     * swallow it.
     */
    startThread: builder.mutation<
      { threadId: string; messageId: string },
      { studentId: string; body: string }
    >({
      query: ({ studentId, body }) => ({
        url: `/students/${studentId}/threads`,
        method: 'POST',
        body: { body },
      }),
      invalidatesTags: (_r, _e, { studentId }) => [
        'Thread',
        { type: 'Thread', id: `STUDENT-${studentId}` },
        'MessageDelivery',
        'MessageUnreadCount',
      ],
    }),

    /**
     * A parent replies to a staff message (announcement or per-child note) about
     * one of their children. Opens a thread back to the sender; the backend
     * rejects it unless the message allows replies and the child is in its scope.
     */
    replyToMessage: builder.mutation<
      { threadId: string },
      { messageId: string; deliveryId: string; studentId: string; body: string }
    >({
      query: ({ messageId, ...body }) => ({
        url: `/messages/${messageId}/replies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MessageDelivery', 'Thread'],
    }),

    /** The threads parents opened in reply to one sent message. Returned whole — not paginated. */
    listMessageReplies: builder.query<
      {
        replies: Array<{
          threadId: string;
          studentId: string;
          studentName: string;
          locationId: string;
          locationName: string;
          status: ThreadStatus;
          lastMessageAt: string;
          lastMessagePreview: string;
        }>;
        nextCursor: null;
      },
      { messageId: string }
    >({
      query: ({ messageId }) => `/messages/${messageId}/replies`,
      providesTags: ['Thread'],
    }),

    /** Post into an existing thread — serves both the parent follow-up and the staff reply. */
    postThreadMessage: builder.mutation<{ messageId: string }, { threadId: string; body: string }>({
      query: ({ threadId, body }) => ({
        url: `/threads/${threadId}/messages`,
        method: 'POST',
        body: { body },
      }),
      invalidatesTags: (_r, _e, { threadId }) => [
        'Thread',
        { type: 'Thread', id: threadId },
        'MessageDelivery',
        'MessageUnreadCount',
      ],
    }),

    /** A thread and its full message history, oldest message first. */
    getThread: builder.query<{ thread: Thread; messages: ThreadMessage[] }, string>({
      query: (threadId) => `/threads/${threadId}`,
      providesTags: (_result, _err, threadId) => [{ type: 'Thread', id: threadId }],
    }),

    /** Every thread about one child — the parent's conversations and the staff per-child history. */
    listStudentThreads: builder.query<{ threads: Thread[] }, string>({
      query: (studentId) => `/students/${studentId}/threads`,
      providesTags: (_result, _err, studentId) => [
        'Thread',
        { type: 'Thread', id: `STUDENT-${studentId}` },
      ],
    }),

    /**
     * The shared staff inbox: every thread at a location, newest activity first.
     *
     * PAGINATION CONTRACT — the backend applies the `status` filter AFTER the
     * DynamoDB page limit, so **a filtered page can come back shorter than the
     * page size (even empty) while `lastKey` is still set**. Drive pagination on
     * the presence of `lastKey` ONLY. Never stop because a page looked short —
     * that silently hides threads.
     *
     * `status: 'ALL'` is the explicit no-filter option; omitting `status`
     * behaves the same but says less.
     */
    listLocationThreads: builder.query<
      { threads: Thread[]; lastKey?: string },
      { locationId: string; status?: ThreadStatus | 'ALL'; cursor?: string }
    >({
      query: ({ locationId, status, cursor }) => {
        const qs = new URLSearchParams();
        if (status) qs.set('status', status);
        if (cursor) qs.set('cursor', cursor);
        const q = qs.toString();
        return `/locations/${locationId}/threads${q ? `?${q}` : ''}`;
      },
      providesTags: (_result, _err, { locationId }) => [
        'Thread',
        { type: 'Thread', id: `LOCATION-${locationId}` },
      ],
    }),

    // ============== Push notifications ==============

    /** Public VAPID key used to subscribe the device. Backend caches 24h. */
    getVapidKey: builder.query<{ publicKey: string }, void>({
      query: () => '/notifications/vapid-key',
    }),

    /** Register this device's push subscription with the backend. */
    subscribePush: builder.mutation<
      { subscriptionId: string; vapidPublicKey: string },
      { endpoint: string; keys: { p256dh: string; auth: string } }
    >({
      query: (body) => ({ url: '/notifications/subscribe', method: 'POST', body }),
    }),

    /** Deregister this device's push subscription. */
    unsubscribePush: builder.mutation<{ message: string }, { endpoint: string }>({
      query: (body) => ({ url: '/notifications/unsubscribe', method: 'POST', body }),
    }),

    /**
     * Fetch the current user's notification preferences.
     *
     * `quietHours` is a window during which push is suppressed — the backend has
     * always enforced it (push-notification-service.isInQuietHours, which handles
     * a window crossing midnight), but no client ever sent or displayed it, so it
     * could not be configured. Null/absent means "no quiet hours".
     */
    getNotificationPreferences: builder.query<
      {
        messages: boolean;
        observations: boolean;
        fireDrills: boolean;
        incidents: boolean;
        quietHours?: { start: string; end: string } | null;
      },
      void
    >({
      query: () => '/notifications/preferences',
      providesTags: ['NotificationPrefs'],
    }),

    /** Update the current user's notification preferences (partial update). */
    updateNotificationPreferences: builder.mutation<
      { message: string },
      {
        messages?: boolean;
        observations?: boolean;
        fireDrills?: boolean;
        incidents?: boolean;
        /** `{ start: 'HH:MM', end: 'HH:MM' }`, or null to clear the window. */
        quietHours?: { start: string; end: string } | null;
      }
    >({
      query: (body) => ({ url: '/notifications/preferences', method: 'PUT', body }),
      invalidatesTags: ['NotificationPrefs'],
    }),

    // ============== Observations ==============

    createObservation: builder.mutation<
      { observation: Observation; recipientCount: number },
      {
        sessionId: string;
        studentId: string;
        text: string;
        photos: ObservationPhoto[];
        visibility: ObservationVisibility;
        /** Aistéar learning-goal ids to tag (under-6 children only; backend
         *  rejects for over-6). Omitted/empty when the picker isn't shown. */
        learningGoalIds?: string[];
      }
    >({
      query: (body) => ({ url: '/observations', method: 'POST', body }),
      invalidatesTags: ['Observation', 'ObservationDelivery', 'ObservationUnreadCount'],
    }),

    getObservation: builder.query<
      {
        observation: Observation;
        photoUrls: string[];
        /** Per-recipient audit projection — present only when the caller is
         *  the author, a location admin at the observation's location, or
         *  super-admin. Parent recipients receive `undefined` to avoid
         *  leaking co-parent read state. Same field set as the messaging
         *  audit projection. */
        deliveries?: Array<
          Pick<
            ObservationDelivery,
            | 'id'
            | 'recipientUserId'
            | 'recipientRole'
            | 'status'
            | 'readAt'
            | 'pushStatus'
            | 'pushError'
          >
        >;
        /** Linked Aistéar goals, resolved server-side (parents hold no
         *  learning-goal:read). Sorted theme → aim → goal; [] when none.
         *  Optional because an older backend omits it. */
        learningGoals?: ObservationLearningGoal[];
      },
      string
    >({
      query: (id) => `/observations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Observation', id }],
    }),

    updateObservation: builder.mutation<
      { observation: Observation; recipientsAdded: number; recipientsRemoved: number },
      {
        id: string;
        text?: string;
        photos?: ObservationPhoto[];
        visibility?: ObservationVisibility;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/observations/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        'Observation',
        { type: 'Observation', id },
        'ObservationDelivery',
        'ObservationUnreadCount',
      ],
    }),

    withdrawObservation: builder.mutation<{ withdrawn: true; deliveriesRemoved: number }, string>({
      query: (id) => ({ url: `/observations/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        'Observation',
        { type: 'Observation', id },
        'ObservationDelivery',
        'ObservationUnreadCount',
      ],
    }),

    listSessionObservations: builder.query<
      { observations: SessionObservationSummary[] },
      { sessionId: string; studentId?: string }
    >({
      query: ({ sessionId, studentId }) => {
        const qs = studentId ? `?studentId=${studentId}` : '';
        return `/sessions/${sessionId}/observations${qs}`;
      },
      providesTags: ['Observation'],
    }),

    listStudentObservations: builder.query<
      { observations: Observation[]; nextCursor: string | null },
      { studentId: string; cursor?: string; limit?: number }
    >({
      query: ({ studentId, cursor, limit }) => {
        const qs = new URLSearchParams();
        if (cursor) qs.set('cursor', cursor);
        if (limit != null) qs.set('limit', String(limit));
        const q = qs.toString();
        return `/students/${studentId}/observations${q ? `?${q}` : ''}`;
      },
      providesTags: ['Observation'],
    }),

    listMyObservations: builder.query<
      { deliveries: ObservationDelivery[]; nextCursor: string | null },
      { status?: MessageDeliveryStatus; cursor?: string; limit?: number } | void
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.cursor) qs.set('cursor', params.cursor);
        if (params?.limit != null) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return `/me/observations${q ? `?${q}` : ''}`;
      },
      providesTags: ['ObservationDelivery'],
    }),

    // `studentId` narrows the count to one child (the per-child Updates badge).
    getMyObservationsUnreadCount: builder.query<{ count: number }, { studentId?: string } | void>({
      query: (args) => ({
        url: '/me/observations/unread-count',
        params: args?.studentId ? { studentId: args.studentId } : undefined,
      }),
      providesTags: ['ObservationUnreadCount'],
    }),

    markObservationRead: builder.mutation<void, string>({
      query: (deliveryId) => ({
        url: `/me/observations/${deliveryId}/read`,
        method: 'POST',
      }),
      invalidatesTags: ['ObservationDelivery', 'ObservationUnreadCount'],
    }),

    // `studentId` clears one child's observations without touching a sibling's.
    markAllObservationsRead: builder.mutation<{ updated: number }, { studentId?: string } | void>({
      query: (args) => ({
        url: '/me/observations/read-all',
        method: 'POST',
        params: args?.studentId ? { studentId: args.studentId } : undefined,
      }),
      invalidatesTags: ['ObservationDelivery', 'ObservationUnreadCount'],
    }),

    // ============== Moments (photo moments + parent feed) ==============

    createMoment: builder.mutation<{ moment: Moment; recipientCount: number }, CreateMomentInput>({
      query: (body) => ({ url: '/moments', method: 'POST', body }),
      // Invalidates SessionSummary so the staff session summary's momentCount
      // refreshes once a moment is recorded.
      invalidatesTags: ['Moment', 'SessionSummary'],
    }),

    // Parent merged "Recent feed" (moments + observations). Provides both
    // source tags so recording either kind refreshes the feed.
    getChildFeed: builder.query<
      { items: FeedItem[]; nextCursor?: string },
      { childId: string; cursor?: string }
    >({
      query: ({ childId, cursor }) =>
        `/me/children/${childId}/feed${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      providesTags: ['Moment', 'Observation'],
    }),

    // Parent moments-only photo grid — a presigned projection
    // (ChildMomentSummary), NOT the full Moment shape.
    listChildMoments: builder.query<
      { moments: ChildMomentSummary[]; nextCursor?: string },
      { childId: string; cursor?: string }
    >({
      query: ({ childId, cursor }) =>
        `/me/children/${childId}/moments${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      providesTags: ['Moment'],
    }),

    // ============== Incidents (parent-facing) ==============
    //
    // Strictly less than the staff incident endpoints — see
    // src/incidents/utils/parent-projection.ts. SUBMITTED and CLOSED only;
    // DRAFT and WITHDRAWN are filtered server-side and never reach here.

    listMyChildIncidents: builder.query<{ incidents: ParentIncident[] }, { childId: string }>({
      query: ({ childId }) => `/me/children/${childId}/incidents`,
      providesTags: (result) =>
        result
          ? [...result.incidents.map((i) => ({ type: 'Incident' as const, id: i.id })), 'Incident']
          : ['Incident'],
    }),

    getMyIncident: builder.query<{ incident: ParentIncident }, string>({
      query: (incidentId) => `/me/incidents/${incidentId}`,
      providesTags: (_r, _e, incidentId) => [{ type: 'Incident', id: incidentId }],
    }),

    acknowledgeIncident: builder.mutation<{ delivery: IncidentDelivery }, string>({
      query: (incidentId) => ({
        url: `/me/incidents/${incidentId}/acknowledge`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, incidentId) => [{ type: 'Incident', id: incidentId }, 'Incident'],
    }),

    // ============== Incidents (staff-facing) ==============
    //
    // The full record — see StaffIncident in types/index.ts. `submitNow: true`
    // creates and submits in ONE round trip, which is what makes the
    // under-2-minutes minor-incident path survive a dropped connection: two
    // separate calls (create-draft, then submit) would leave an orphan DRAFT
    // if the network dropped between them.
    createIncident: builder.mutation<
      { incident: StaffIncident; recipientCount: number },
      CreateIncidentInput
    >({
      query: (body) => ({
        url: '/incidents',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Incident'],
    }),

    // GET /locations/{locationId}/incidents — the staff/admin incident
    // register. Every filter is a query param, evaluated by DynamoDB
    // server-side (see list-location-incidents' handler doc comment) — never
    // filter the returned array client-side, that would defeat the point.
    listLocationIncidents: builder.query<
      { incidents: StaffIncident[] },
      { locationId: string } & IncidentRegisterFilters
    >({
      query: ({ locationId, ...filters }) => {
        const qs = new URLSearchParams();
        if (filters.fromDate) qs.set('fromDate', filters.fromDate);
        if (filters.toDate) qs.set('toDate', filters.toDate);
        if (filters.severity) qs.set('severity', filters.severity);
        if (filters.incidentType) qs.set('incidentType', filters.incidentType);
        if (filters.status) qs.set('status', filters.status);
        if (filters.childId) qs.set('childId', filters.childId);
        const q = qs.toString();
        return `/locations/${locationId}/incidents${q ? `?${q}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [...result.incidents.map((i) => ({ type: 'Incident' as const, id: i.id })), 'Incident']
          : ['Incident'],
    }),

    // GET /students/{studentId}/incidents — the STAFF per-child history (full
    // visibility, incl. DRAFT/WITHDRAWN) — distinct from
    // useListMyChildIncidentsQuery, which is the parent-safe projection.
    // No childId filter param here: the endpoint is already scoped to one
    // student (see the handler's doc comment).
    listStudentIncidents: builder.query<
      { incidents: StaffIncident[] },
      { studentId: string } & Omit<IncidentRegisterFilters, 'childId'>
    >({
      query: ({ studentId, ...filters }) => {
        const qs = new URLSearchParams();
        if (filters.fromDate) qs.set('fromDate', filters.fromDate);
        if (filters.toDate) qs.set('toDate', filters.toDate);
        if (filters.severity) qs.set('severity', filters.severity);
        if (filters.incidentType) qs.set('incidentType', filters.incidentType);
        if (filters.status) qs.set('status', filters.status);
        const q = qs.toString();
        return `/students/${studentId}/incidents${q ? `?${q}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [...result.incidents.map((i) => ({ type: 'Incident' as const, id: i.id })), 'Incident']
          : ['Incident'],
    }),

    // GET /incidents/{incidentId} — the full staff/admin detail: amendments,
    // siblings (other children in the same group event), and per-guardian
    // delivery/acknowledgment status. NOT the parent detail
    // (useGetMyIncidentQuery) — that's a strictly-smaller projection.
    getIncident: builder.query<IncidentDetailResponse, string>({
      query: (incidentId) => `/incidents/${incidentId}`,
      providesTags: (_r, _e, incidentId) => [{ type: 'Incident', id: incidentId }],
    }),

    // PUT /incidents/{incidentId} — used ONLY to backfill `incidentGroupId`
    // onto the SOURCE incident of a "record for another child" flow (spec
    // §1) when it didn't already have one. Without this, generating a fresh
    // group id and stamping it on the NEW record alone does not "retroactively
    // join" anything — the source incident's own siblings lookup is keyed on
    // ITS OWN incidentGroupId field, which stays undefined unless patched.
    // Every other editable field on this endpoint is intentionally left
    // untouched by this call.
    setIncidentGroupId: builder.mutation<
      { incident: StaffIncident },
      { incidentId: string; incidentGroupId: string }
    >({
      query: ({ incidentId, incidentGroupId }) => ({
        url: `/incidents/${incidentId}`,
        method: 'PUT',
        body: { incidentGroupId },
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [{ type: 'Incident', id: incidentId }],
    }),

    // POST /incidents/{incidentId}/amend — append-only correction (NOTE
    // only; the other amendment kinds are written internally by the
    // severity/tusla-reported/withdraw handlers, never by this endpoint).
    amendIncident: builder.mutation<
      { amendment: IncidentAmendment },
      { incidentId: string; text: string }
    >({
      query: ({ incidentId, text }) => ({
        url: `/incidents/${incidentId}/amend`,
        method: 'POST',
        body: { kind: 'NOTE', text },
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [{ type: 'Incident', id: incidentId }],
    }),

    // POST /incidents/{incidentId}/submit — promotes a DRAFT into a real
    // (SUBMITTED) statutory record and fans out guardian notifications.
    // Invalidates the Incident id tag (detail refetch) + the 'Incident' list
    // tag the register/history lists provide, so the row's new status shows
    // everywhere — same invalidation contract as amendIncident.
    submitIncident: builder.mutation<
      { incident: StaffIncident; recipientCount: number; overdueOnSubmit: boolean },
      { incidentId: string }
    >({
      query: ({ incidentId }) => ({
        url: `/incidents/${incidentId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [
        { type: 'Incident', id: incidentId },
        'Incident',
      ],
    }),

    // DELETE /incidents/{incidentId} — HARD-deletes a DRAFT (author or admin).
    // Only DRAFTs are deletable; SUBMITTED+ are statutory records the backend
    // refuses to delete. Returns 204 (no body).
    discardIncidentDraft: builder.mutation<void, { incidentId: string }>({
      query: ({ incidentId }) => ({
        url: `/incidents/${incidentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [
        { type: 'Incident', id: incidentId },
        'Incident',
      ],
    }),

    // POST /incidents/{incidentId}/withdraw — a STATUS CHANGE, never a delete.
    // The record stays in the register flagged WITHDRAWN with the reason.
    // `reason` is required (server rejects an empty reason).
    withdrawIncident: builder.mutation<
      { incident: StaffIncident; amendment: IncidentAmendment },
      { incidentId: string; reason: string }
    >({
      query: ({ incidentId, reason }) => ({
        url: `/incidents/${incidentId}/withdraw`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [
        { type: 'Incident', id: incidentId },
        'Incident',
      ],
    }),

    // POST /incidents/{incidentId}/close — the terminal "handled" state. No
    // body. A NOTIFIABLE incident is refused (409 TUSLA_REPORT_REQUIRED) until
    // `tuslaReportedAt` is set — that error is surfaced to the user by the
    // caller rather than swallowed.
    closeIncident: builder.mutation<{ incident: StaffIncident }, { incidentId: string }>({
      query: ({ incidentId }) => ({
        url: `/incidents/${incidentId}/close`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [
        { type: 'Incident', id: incidentId },
        'Incident',
      ],
    }),

    // POST /incidents/{incidentId}/severity — the ONLY path that may change
    // severity. Body is { to, notifiableCategory?, reason } — note the field is
    // `to` (the NEW severity), not `newSeverity`. Escalating a CLOSED incident
    // reopens it to SUBMITTED (the handler does that). `reason` is required.
    changeIncidentSeverity: builder.mutation<
      { incident: StaffIncident; amendment: IncidentAmendment },
      {
        incidentId: string;
        to: IncidentSeverity;
        notifiableCategory?: NotifiableCategory;
        reason: string;
      }
    >({
      query: ({ incidentId, ...body }) => ({
        url: `/incidents/${incidentId}/severity`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [
        { type: 'Incident', id: incidentId },
        'Incident',
      ],
    }),

    // POST /incidents/{incidentId}/tusla-reported — records that the
    // statutory Tusla notification has been filed. Drives the Tusla panel's
    // "mark reported" action for a NOTIFIABLE incident.
    tuslaReportedIncident: builder.mutation<
      { incident: StaffIncident; amendment: IncidentAmendment },
      { incidentId: string; tuslaReference: string }
    >({
      query: ({ incidentId, tuslaReference }) => ({
        url: `/incidents/${incidentId}/tusla-reported`,
        method: 'POST',
        body: { tuslaReference },
      }),
      invalidatesTags: (_r, _e, { incidentId }) => [{ type: 'Incident', id: incidentId }],
    }),

    // GET /organizations/{organizationId}/incident-fields — the org's
    // per-org custom incident field config (Task 24). Read by the create
    // modal / detail / admin surface alike, so a config change invalidates
    // everywhere it's read.
    getIncidentFields: builder.query<{ config: IncidentFieldsConfig }, { organizationId: string }>({
      query: ({ organizationId }) => `/organizations/${organizationId}/incident-fields`,
      providesTags: ['IncidentFieldsConfig'],
    }),

    // PUT /organizations/{organizationId}/incident-fields — replaces the
    // whole config (org-admin only; enforced server-side).
    updateIncidentFields: builder.mutation<
      { config: IncidentFieldsConfig },
      { organizationId: string; fields: IncidentFieldsConfig['fields'] }
    >({
      query: ({ organizationId, fields }) => ({
        url: `/organizations/${organizationId}/incident-fields`,
        method: 'PUT',
        body: { fields },
      }),
      invalidatesTags: ['IncidentFieldsConfig'],
    }),

    // ============== Fire Drills ==============

    startFireDrill: builder.mutation<
      { drill: FireDrill },
      {
        locationId: string;
        type: FireDrillType;
        scope: FireDrillScope;
        classId?: string;
        assemblyPoint: string;
        exitRoute: string;
        scenario?: string;
        isLiveRecording: boolean;
        startedAt?: string;
        completedAt?: string;
        staffIds: string[];
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/fire-drills`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FireDrill', 'FireDrillCadence'],
    }),

    getFireDrill: builder.query<
      {
        drill: FireDrill;
        audit?: { edits: FireDrillEditEntry[]; withdrawnAt?: string; withdrawnBy?: string };
      },
      string
    >({
      query: (id) => `/fire-drills/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'FireDrill', id }],
    }),

    updateFireDrill: builder.mutation<
      { drill: FireDrill },
      {
        id: string;
        notes?: string;
        assemblyPoint?: string;
        exitRoute?: string;
        scenario?: string;
        signOff?: true;
        issuesToAdd?: { category: FireDrillIssueCategory; description: string }[];
      }
    >({
      query: ({ id, ...body }) => ({ url: `/fire-drills/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => ['FireDrill', { type: 'FireDrill', id }],
    }),

    tickFireDrillAttendee: builder.mutation<
      void,
      {
        drillId: string;
        studentId: string;
        accountedFor: boolean;
        secondsToAccount?: number;
        unaccountedReason?: string;
      }
    >({
      query: ({ drillId, studentId, ...body }) => ({
        url: `/fire-drills/${drillId}/attendees/${studentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { drillId }) => [{ type: 'FireDrill', id: drillId }],
    }),

    completeFireDrill: builder.mutation<{ drill: FireDrill }, { id: string; completedAt?: string }>(
      {
        query: ({ id, ...body }) => ({
          url: `/fire-drills/${id}/complete`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_r, _e, { id }) => [
          'FireDrill',
          { type: 'FireDrill', id },
          'FireDrillCadence',
        ],
      }
    ),

    resolveFireDrillIssue: builder.mutation<
      void,
      { drillId: string; issueId: string; resolutionNote: string }
    >({
      query: ({ drillId, issueId, resolutionNote }) => ({
        url: `/fire-drills/${drillId}/issues/${issueId}`,
        method: 'PUT',
        body: { resolutionNote },
      }),
      invalidatesTags: (_r, _e, { drillId }) => [
        { type: 'FireDrill', id: drillId },
        'FireDrillCadence',
      ],
    }),

    withdrawFireDrill: builder.mutation<void, string>({
      query: (id) => ({ url: `/fire-drills/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => ['FireDrill', { type: 'FireDrill', id }, 'FireDrillCadence'],
    }),

    listFireDrills: builder.query<
      { drills: FireDrill[]; cadence: FireDrillCadence; nextCursor: string | null },
      { locationId: string; cursor?: string; limit?: number }
    >({
      query: ({ locationId, cursor, limit }) => {
        const qs = new URLSearchParams();
        if (cursor) qs.set('cursor', cursor);
        if (limit != null) qs.set('limit', String(limit));
        const q = qs.toString();
        return `/locations/${locationId}/fire-drills${q ? `?${q}` : ''}`;
      },
      providesTags: ['FireDrill', 'FireDrillCadence'],
    }),

    // ============== Student Fees ==============
    listStudentFees: builder.query<{ fees: import('../../types/fee').Fee[] }, string>({
      query: (studentId) => `/students/${studentId}/fees`,
      providesTags: (_result, _err, studentId) => [{ type: 'Fee' as const, id: studentId }],
    }),

    createFee: builder.mutation<
      { fee: import('../../types/fee').Fee },
      { studentId: string; label: string; amount: number }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/students/${studentId}/fees`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'Fee' as const, id: studentId }],
    }),

    updateFee: builder.mutation<
      { fee: import('../../types/fee').Fee },
      { studentId: string; feeId: string; label?: string; amount?: number }
    >({
      query: ({ studentId, feeId, ...body }) => ({
        url: `/students/${studentId}/fees/${feeId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'Fee' as const, id: studentId }],
    }),

    deleteFee: builder.mutation<void, { studentId: string; feeId: string }>({
      query: ({ studentId, feeId }) => ({
        url: `/students/${studentId}/fees/${feeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { studentId }) => [{ type: 'Fee' as const, id: studentId }],
    }),

    // ============== Cleaning Schedules ==============

    listCleaningAreas: builder.query<{ areas: CleaningAreaWithStatus[] }, { locationId: string }>({
      query: ({ locationId }) => `/locations/${locationId}/cleaning-areas`,
      providesTags: ['CleaningArea'],
    }),

    createCleaningArea: builder.mutation<
      { area: CleaningArea },
      {
        locationId: string;
        name: string;
        zone?: string;
        frequency: CleaningFrequency;
        isHighRisk: boolean;
        notes?: string;
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/cleaning-areas`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CleaningArea'],
    }),

    updateCleaningArea: builder.mutation<
      { area: CleaningArea },
      {
        locationId: string;
        areaId: string;
        name?: string;
        /** '' clears the zone. */
        zone?: string;
        frequency?: CleaningFrequency;
        isHighRisk?: boolean;
        notes?: string;
        isActive?: boolean;
      }
    >({
      query: ({ locationId, areaId, ...body }) => ({
        url: `/locations/${locationId}/cleaning-areas/${areaId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CleaningArea'],
    }),

    createCleaningCompletion: builder.mutation<
      { completion: CleaningCompletion },
      { locationId: string; areaId: string; note?: string; productUsed?: string }
    >({
      query: ({ locationId, areaId, ...body }) => ({
        url: `/locations/${locationId}/cleaning-areas/${areaId}/completions`,
        method: 'POST',
        body,
      }),
      // Recording a completion changes BOTH lists on the cleaning page: the
      // area's status ("OVERDUE" → "OK") and the inspection export below it.
      // Invalidating only CleaningArea left the export showing "No completions
      // in this date range" immediately after a completion that was inside the
      // range — it appeared only after a manual reload. That export is the
      // Tusla-facing view, so silently omitting a just-recorded clean is the
      // worst place for a stale cache.
      invalidatesTags: ['CleaningArea', 'CleaningCompletion'],
    }),

    listCleaningCompletions: builder.query<
      { completions: CleaningCompletion[] },
      { locationId: string; from?: string; to?: string }
    >({
      query: ({ locationId, from, to }) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const q = qs.toString();
        return `/locations/${locationId}/cleaning-completions${q ? `?${q}` : ''}`;
      },
      providesTags: ['CleaningCompletion'],
    }),

    // ============== Learning Goals (Aistéar) ==============

    listLearningGoals: builder.query<
      { goals: LearningGoal[] },
      { organizationId: string; framework?: LearningGoalFramework }
    >({
      query: ({ organizationId, framework }) => {
        const qs = framework ? `?framework=${framework}` : '';
        return `/organizations/${organizationId}/learning-goals${qs}`;
      },
      providesTags: ['LearningGoal'],
    }),

    createLearningGoal: builder.mutation<
      { goal: LearningGoal },
      {
        organizationId: string;
        theme: string;
        aimNumber: number;
        aimText: string;
        goalIndex: number;
        text: string;
      }
    >({
      query: ({ organizationId, ...body }) => ({
        url: `/organizations/${organizationId}/learning-goals`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LearningGoal'],
    }),

    updateLearningGoal: builder.mutation<
      { goal: LearningGoal },
      {
        organizationId: string;
        goalId: string;
        text?: string;
        aimText?: string;
        isActive?: boolean;
      }
    >({
      query: ({ organizationId, goalId, ...body }) => ({
        url: `/organizations/${organizationId}/learning-goals/${goalId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['LearningGoal'],
    }),

    getStudentLearningGoalCoverage: builder.query<LearningGoalCoverage, { studentId: string }>({
      query: ({ studentId }) => `/students/${studentId}/learning-goal-coverage`,
      providesTags: ['LearningGoal'],
    }),

    // ===================== Retention (Task 21 — clocks only) =================
    // Nothing in these endpoints deletes data. The schedule is the editable
    // record-type → period map with honest provenance; the overview COUNTS
    // records past their computed expiry (informational); legal hold freezes a
    // child's clocks.

    getRetentionSchedule: builder.query<
      { schedule: ResolvedRetentionRule[] },
      { locationId: string }
    >({
      query: ({ locationId }) => `/locations/${locationId}/retention/schedule`,
      providesTags: ['RetentionSchedule'],
    }),

    updateRetentionRule: builder.mutation<
      { rule: ResolvedRetentionRule },
      { locationId: string; recordType: RetentionRecordType; periodMonths: number | null }
    >({
      query: ({ locationId, recordType, periodMonths }) => ({
        url: `/locations/${locationId}/retention/schedule/${recordType}`,
        method: 'PUT',
        body: { periodMonths },
      }),
      // Editing a period changes what the overview would count.
      invalidatesTags: ['RetentionSchedule', 'RetentionOverview'],
    }),

    getRetentionOverview: builder.query<
      { overview: RetentionOverviewRow[]; today: string; note: string },
      { locationId: string }
    >({
      query: ({ locationId }) => `/locations/${locationId}/retention/overview`,
      providesTags: ['RetentionOverview'],
    }),

    setStudentLegalHold: builder.mutation<
      { message: string; studentId: string; legalHold: StudentLegalHold },
      { studentId: string; reason: string }
    >({
      query: ({ studentId, reason }) => ({
        url: `/students/${studentId}/legal-hold`,
        method: 'POST',
        body: { reason },
      }),
      // A held child drops out of the overview's past-expiry counts.
      invalidatesTags: ['Student', 'RetentionOverview'],
    }),

    clearStudentLegalHold: builder.mutation<
      { message: string; studentId: string },
      { studentId: string }
    >({
      query: ({ studentId }) => ({
        url: `/students/${studentId}/legal-hold`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Student', 'RetentionOverview'],
    }),

    // ============== Medication Requirements & Administration Log ==============

    // POST /students/{studentId}/medications — create a requirement.
    createMedication: builder.mutation<
      { requirement: MedicationRequirement },
      {
        studentId: string;
        medicationName: string;
        requirementType: MedicationRequirementType;
        dosage: string;
        route: MedicationRoute;
        schedule?: MedicationSchedule | null;
        emergencyInstructions?: string;
        prescriber?: string;
        startDate: string;
        endDate?: string;
        medicationExpiryDate?: string;
        specialInstructions?: string;
        attachments?: string[];
        locationId: string;
        consentConfirmed?: boolean;
      }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/students/${studentId}/medications`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Medication'],
    }),

    // POST /medications/{medicationId}/consent — guardian confirms consent.
    consentMedication: builder.mutation<{ requirement: MedicationRequirement }, string>({
      query: (medicationId) => ({
        url: `/medications/${medicationId}/consent`,
        method: 'POST',
      }),
      invalidatesTags: ['Medication'],
    }),

    // POST /medications/{medicationId}/revoke — guardian withdraws consent.
    revokeMedication: builder.mutation<
      { requirement: MedicationRequirement },
      { medicationId: string; reason?: string }
    >({
      query: ({ medicationId, reason }) => ({
        url: `/medications/${medicationId}/revoke`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['Medication'],
    }),

    // POST /medications/{medicationId}/end — course of medication finished.
    endMedication: builder.mutation<
      { requirement: MedicationRequirement },
      { medicationId: string; reason?: string }
    >({
      query: ({ medicationId, reason }) => ({
        url: `/medications/${medicationId}/end`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['Medication'],
    }),

    // GET /students/{studentId}/medications — staff view: every requirement
    // for the child, each with its full (raw) administration log.
    listStudentMedications: builder.query<
      { requirements: MedicationRequirementWithEntries[] },
      { studentId: string }
    >({
      query: ({ studentId }) => `/students/${studentId}/medications`,
      providesTags: (result) =>
        result
          ? [
              ...result.requirements.map((r) => ({
                type: 'Medication' as const,
                id: r.requirementId,
              })),
              'Medication',
            ]
          : ['Medication'],
    }),

    // GET /me/children/{childId}/medications — parent view: the allowlist
    // projection (see ParentSafeMedication — no attachments, no tenancy
    // plumbing).
    listMyChildMedications: builder.query<
      { requirements: ParentSafeMedication[] },
      { childId: string }
    >({
      query: ({ childId }) => `/me/children/${childId}/medications`,
      providesTags: (result) =>
        result
          ? [
              ...result.requirements.map((r) => ({
                type: 'Medication' as const,
                id: r.requirementId,
              })),
              'Medication',
            ]
          : ['Medication'],
    }),

    // POST /medications/{medicationId}/administrations — staff logs a dose.
    // Invalidates BOTH tags: logging a dose changes the log (Medication) AND
    // the due-today board (MedicationsDue).
    administerMedication: builder.mutation<
      { entry: MedicationAdministrationEntry },
      {
        medicationId: string;
        entryType: MedicationEntryType;
        administeredAt: string;
        doseGiven?: string;
        witnessStaffId?: string;
        unwitnessedReason?: string;
        correctsEntryId?: string;
        notes?: string;
        overrideReason?: string;
      }
    >({
      query: ({ medicationId, ...body }) => ({
        url: `/medications/${medicationId}/administrations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { medicationId }) => [
        { type: 'Medication' as const, id: medicationId },
        'Medication',
        'MedicationsDue',
      ],
    }),

    // GET /sessions/{sessionId}/medications-due — the medication board for
    // the children present in one session.
    sessionMedicationsDue: builder.query<
      {
        date: string;
        sessionId: string;
        slots: MedicationDueSlot[];
        badges: MedicationEmergencyBadge[];
      },
      string
    >({
      query: (sessionId) => `/sessions/${sessionId}/medications-due`,
      providesTags: ['MedicationsDue'],
    }),

    // GET /sessions/{sessionId}/summary — activity roll-up for one session
    // (observations + incidents + meds given). Provides all three source tags
    // so recording any of them refreshes the strip live.
    sessionSummary: builder.query<SessionSummary, string>({
      query: (sessionId) => `/sessions/${sessionId}/summary`,
      providesTags: ['Observation', 'Incident', 'MedicationsDue', 'Moment'],
    }),

    // GET /locations/{locationId}/medications-due — the whole-location
    // medication board for today.
    locationMedicationsDue: builder.query<
      { date: string; slots: MedicationDueSlot[]; badges: MedicationEmergencyBadge[] },
      string
    >({
      query: (locationId) => `/locations/${locationId}/medications-due`,
      providesTags: ['MedicationsDue'],
    }),

    // ---- Policy & Document Vault ----

    // GET /locations/{locationId}/policies — the register. Always returns the
    // nine checklist slots (NOT_ADDED when unfilled) plus custom policies.
    getLocationPolicies: builder.query<{ register: PolicyRegisterRow[] }, string>({
      query: (locationId) => `/locations/${locationId}/policies`,
      providesTags: ['PolicyRegister'],
    }),

    getPolicy: builder.query<
      { policy: Policy; versions: PolicyVersion[]; reviews: PolicyReviewRecord[] },
      string
    >({
      query: (policyId) => `/policies/${policyId}`,
      providesTags: ['Policy'],
    }),

    createPolicy: builder.mutation<
      { policy: Policy },
      {
        locationId: string;
        checklistKey?: PolicyChecklistKey;
        title: string;
        summary?: string;
        staffAckRequired?: boolean;
        audience?: PolicyAudience;
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/policies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolicyRegister'],
    }),

    // Settings only. parentAction is deliberately NOT here — changing the ask
    // requires publishing a version, so a dropdown cannot mass-notify.
    // `audience` changes who is counted and asked, so coverage and the
    // parents' lists are invalidated too.
    updatePolicy: builder.mutation<
      { policy: Policy },
      {
        policyId: string;
        title?: string;
        summary?: string;
        staffAckRequired?: boolean;
        reviewRemindersEnabled?: boolean;
        audience?: PolicyAudience;
      }
    >({
      query: ({ policyId, ...body }) => ({
        url: `/policies/${policyId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PolicyRegister', 'Policy', 'PolicyCoverage', 'MyPolicies'],
    }),

    setPolicyPaperBased: builder.mutation<
      { policy: Policy },
      { policyId: string; paperBased: boolean; note?: string; reviewRemindersEnabled?: boolean }
    >({
      query: ({ policyId, ...body }) => ({
        url: `/policies/${policyId}/paper-based`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolicyRegister', 'Policy'],
    }),

    // Publishing changes what everyone is asked, so it invalidates everything.
    createPolicyVersion: builder.mutation<
      { version: PolicyVersion },
      {
        policyId: string;
        fileKey?: string;
        childFriendlyFileKey?: string;
        parentAction: ParentAction;
        effectiveDate: string;
        changeNote?: string;
        materialChange?: boolean;
        isSettingsOnly?: boolean;
        /** Absent keeps the stored audience (the settings-only republish relies on this). */
        audience?: PolicyAudience;
      }
    >({
      query: ({ policyId, ...body }) => ({
        url: `/policies/${policyId}/versions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolicyRegister', 'Policy', 'MyPolicies', 'PolicyCoverage'],
    }),

    recordPolicyReview: builder.mutation<
      { review: PolicyReviewRecord },
      { policyId: string; note?: string }
    >({
      query: ({ policyId, ...body }) => ({
        url: `/policies/${policyId}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolicyRegister', 'Policy'],
    }),

    // childId is required for a parent; staff omit it and acknowledge for
    // themselves.
    acknowledgePolicy: builder.mutation<
      { acknowledgment: { ackId: string } },
      { policyId: string; childId?: string; signedName?: string }
    >({
      query: ({ policyId, ...body }) => ({
        url: `/policies/${policyId}/acknowledge`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { childId }) => {
        const tags: any[] = [
          'MyPolicies',
          'PolicyCoverage',
          { type: 'StudentPolicies', id: 'LIST' },
        ];
        if (childId) {
          tags.push({ type: 'StudentPolicies', id: childId });
        }
        return tags;
      },
    }),

    getMyPolicies: builder.query<{ policies: ParentSafePolicy[] | StaffPolicyRow[] }, void>({
      query: () => '/me/policies',
      providesTags: ['MyPolicies'],
    }),

    listStudentPolicies: builder.query<
      { acceptances: StudentPolicyAcceptance[] },
      { studentId: string }
    >({
      query: ({ studentId }) => `/students/${studentId}/policies`,
      providesTags: (_r, _e, { studentId }) => [{ type: 'StudentPolicies', id: studentId }],
    }),

    getPolicyCoverage: builder.query<PolicyCoverage, string>({
      query: (policyId) => `/policies/${policyId}/coverage`,
      providesTags: ['PolicyCoverage'],
    }),

    getPolicySignatures: builder.query<
      PolicySignatures,
      { policyId: string; versionId?: string; audience?: 'PARENT' | 'STAFF' }
    >({
      query: ({ policyId, versionId, audience }) => {
        const qs = new URLSearchParams();
        if (versionId) qs.set('versionId', versionId);
        if (audience) qs.set('audience', audience);
        const s = qs.toString();
        return `/policies/${policyId}/signatures${s ? `?${s}` : ''}`;
      },
      providesTags: ['PolicyCoverage'],
    }),

    // CSV export — returns a Blob so the caller can pipe it to a download link.
    // The server names the file (`<policy>-<version date>-signatures.csv`),
    // so the filename comes back alongside the blob rather than being
    // reinvented at the call site — the caller can't know the version date.
    exportPolicySignatures: builder.mutation<
      { blob: Blob; filename: string },
      { policyId: string; view: 'signed' | 'outstanding'; versionId?: string }
    >({
      query: ({ policyId, view, versionId }) => {
        const qs = new URLSearchParams({ view });
        if (versionId) qs.set('versionId', versionId);
        return {
          url: `/policies/${policyId}/signatures/export?${qs.toString()}`,
          method: 'GET',
          responseHandler: async (response: Response) => ({
            blob: await response.blob(),
            filename: filenameFromContentDisposition(response.headers.get('content-disposition')),
          }),
          cache: 'no-cache',
        };
      },
    }),

    nudgePolicy: builder.mutation<{ notified: number; outstandingChildren: number }, string>({
      query: (policyId) => ({ url: `/policies/${policyId}/nudge`, method: 'POST' }),
      invalidatesTags: ['Policy'],
    }),

    // ---- Daily Checklists ----

    // GET /locations/{locationId}/checklist-items — the admin-defined
    // template, active items only.
    listChecklistItems: builder.query<{ items: ChecklistItem[] }, string>({
      query: (locationId) => `/locations/${locationId}/checklist-items`,
      providesTags: ['ChecklistItem'],
    }),

    createChecklistItem: builder.mutation<
      { item: ChecklistItem },
      {
        locationId: string;
        kind: ChecklistKind;
        label: string;
        order: number;
        isRequired?: boolean;
      }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/checklist-items`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChecklistItem'],
    }),

    // Editing the template also invalidates ChecklistRun: a new run
    // snapshots the item list at creation, so a stale template item shape
    // would carry into the next run started after this edit.
    updateChecklistItem: builder.mutation<
      { item: ChecklistItem },
      { itemId: string; label?: string; order?: number; isRequired?: boolean; isActive?: boolean }
    >({
      query: ({ itemId, ...body }) => ({
        url: `/checklist-items/${itemId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ChecklistItem', 'ChecklistRun'],
    }),

    // GET /locations/{locationId}/checklist-runs?from&to — flat rows, no
    // {run:{...}} nesting.
    listChecklistRuns: builder.query<
      { runs: ClientChecklistRun[] },
      { locationId: string; from: string; to: string }
    >({
      query: ({ locationId, from, to }) => ({
        url: `/locations/${locationId}/checklist-runs`,
        params: { from, to },
      }),
      providesTags: ['ChecklistRun'],
    }),

    // GET /checklist-runs/unsigned?locationId= — the admin sign-off queue,
    // sourced from the sparse GSI2 rather than a client-side filter over
    // listChecklistRuns: no lookback window, and locationId omitted spans
    // every location the caller's role can sign off (never client-scoped —
    // the server derives the caller's own tenant/location).
    listUnsignedChecklistRuns: builder.query<
      { runs: ClientChecklistRun[] },
      { locationId?: string } | void
    >({
      query: (args) => ({
        url: '/checklist-runs/unsigned',
        params: args?.locationId ? { locationId: args.locationId } : undefined,
      }),
      providesTags: ['ChecklistQueue'],
    }),

    // A mutation, not a query: it can write (create today's run on first
    // call). 201 on creation, 200 when the day's run already exists.
    getOrCreateChecklistRun: builder.mutation<
      { run: ClientChecklistRun },
      { locationId: string; kind: ChecklistKind }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/checklist-runs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChecklistRun', 'ChecklistQueue'],
    }),

    // A run becoming COMPLETE changes what the admin sign-off queue shows.
    // Answers and/or the run note in one PATCH; the backend refuses a body
    // with neither, and refuses a No that has no note (or legacy comment).
    answerChecklistItems: builder.mutation<
      { run: ClientChecklistRun },
      {
        runId: string;
        answers?: { itemId: string; value: ChecklistAnswerValue; comment?: string }[];
        note?: string;
      }
    >({
      query: ({ runId, ...body }) => ({
        url: `/checklist-runs/${runId}/answers`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ChecklistRun', 'ChecklistQueue'],
    }),

    signOffChecklistRuns: builder.mutation<
      { signed: number; skipped: number; incomplete: string[] },
      { runIds: string[] }
    >({
      query: (body) => ({
        url: `/checklist-runs/sign-off`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChecklistItem', 'ChecklistRun', 'ChecklistQueue'],
    }),

    // ---- Staff Compliance (Garda Vetting & Credentials) ----

    // GET /staff/{staffUserId}/compliance — one person's full profile.
    // vettingStatus is derived fresh on every read and may be any of the
    // four VettingStatus members, including NOT_RECORDED and REVET_DUE.
    getStaffCompliance: builder.query<StaffComplianceProfile, string>({
      query: (staffUserId) => `/staff/${staffUserId}/compliance`,
      providesTags: ['StaffCompliance'],
    }),

    // GET /locations/{locationId}/staff-compliance — the register, one row
    // per person (not per record).
    listStaffComplianceRegister: builder.query<
      { register: StaffComplianceRegisterEntry[] },
      string
    >({
      query: (locationId) => `/locations/${locationId}/staff-compliance`,
      providesTags: ['StaffCompliance'],
    }),

    // POST /staff/{staffUserId}/compliance/vetting — admin-only, always
    // appends a new row (a re-vet keeps history rather than overwriting it).
    upsertVetting: builder.mutation<
      { vetting: ClientVettingRecord },
      {
        staffUserId: string;
        vettingStatus: WritableVettingStatus;
        disclosureDate?: string;
        nvbReference?: string;
        vettedThrough?: string;
        decision?: 'SATISFACTORY';
        revetDueAt?: string;
        revetDueOverridden?: boolean;
        attachmentS3Key?: string;
      }
    >({
      query: ({ staffUserId, ...body }) => ({
        url: `/staff/${staffUserId}/compliance/vetting`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StaffCompliance'],
    }),

    // POST /staff/{staffUserId}/compliance/credentials — create/update a
    // credential. Self-submissions are forced to submittedBySelf: true and
    // verified: false server-side; neither field is ever accepted from here.
    upsertCredential: builder.mutation<
      { credential: ClientCredentialRecord },
      {
        staffUserId: string;
        credentialType: CredentialType | string;
        title: string;
        issuer?: string;
        awardedDate?: string;
        expiryDate?: string;
        attachmentS3Key?: string;
      }
    >({
      query: ({ staffUserId, ...body }) => ({
        url: `/staff/${staffUserId}/compliance/credentials`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StaffCompliance'],
    }),

    // POST /staff/{staffUserId}/compliance/credentials/{credentialId}/verify
    // — admin-only countersign of a (typically self-submitted) credential.
    verifyCredential: builder.mutation<
      { credential: ClientCredentialRecord },
      { staffUserId: string; credentialId: string }
    >({
      query: ({ staffUserId, credentialId }) => ({
        url: `/staff/${staffUserId}/compliance/credentials/${credentialId}/verify`,
        method: 'POST',
      }),
      invalidatesTags: ['StaffCompliance'],
    }),

    // POST /staff/{staffUserId}/compliance/references — admin-only, no
    // self-submit variant.
    upsertReference: builder.mutation<
      { reference: ClientReferenceCheck },
      {
        staffUserId: string;
        refereeName: string;
        refereeOrganisation?: string;
        note?: string;
      }
    >({
      query: ({ staffUserId, ...body }) => ({
        url: `/staff/${staffUserId}/compliance/references`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StaffCompliance'],
    }),

    // GET /locations/{locationId}/staff-compliance/expected-items — which
    // credential types this location expects, and whether it expects
    // vetting. A location that has never configured one gets a neutral
    // default shape back (nothing expected yet), never a 404.
    getExpectedItems: builder.query<{ expectedItems: StaffExpectedItemsConfig }, string>({
      query: (locationId) => `/locations/${locationId}/staff-compliance/expected-items`,
      providesTags: ['StaffCompliance'],
    }),

    // GET /staff/{staffUserId}/compliance/attachments/{recordId} — modeled as
    // a mutation, not a query, even though it's a GET: it mints a fresh
    // 15-minute presigned URL on every call and must never be cached or
    // deduplicated by RTK Query. Same precedent as closureImpact above.
    getAttachmentUrl: builder.mutation<
      { url: string; expiresIn: number },
      { staffUserId: string; recordId: string }
    >({
      query: ({ staffUserId, recordId }) => ({
        url: `/staff/${staffUserId}/compliance/attachments/${recordId}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLazyGetPolicyVersionFileQuery,
  useLoginMutation: useLocalLoginMutation,
  useMfaChallengeMutation,
  useMfaSetupStartMutation,
  useMfaSetupVerifyMutation,
  useMfaStatusQuery,
  useMfaDisableMutation,
  useMfaAdminResetMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useChangePasswordMutation,
  useGetDashboardStatsQuery,
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
  useCreateOrganizationMutation,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useReportPlannedAbsenceMutation,
  useCancelPlannedAbsenceMutation,
  useGetCalendarQuery,
  useGetAttendanceRegisterQuery,
  useGetAttendanceConcernsQuery,
  useGetStudentAttendanceHistoryQuery,
  useListClosuresQuery,
  useClosureImpactMutation,
  useCreateClosureMutation,
  useUpdateClosureMutation,
  useDeleteClosureMutation,
  useGetClassesQuery,
  useGetClassCapacityQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useCloseClassMutation,
  useCreateTermMutation,
  useUpdateTermMutation,
  useDeleteTermMutation,
  useGetClassInstancesQuery,
  useSetClassOverrideMutation,
  useRemoveClassOverrideMutation,
  useGetClassStudentsQuery,
  useEnrollStudentMutation,
  useUnenrollStudentMutation,
  useUpdateEnrollmentDaysMutation,
  useGetClassTermsQuery,
  useEndEnrollmentMutation,
  useCancelBookingMutation,
  useTransferStudentMutation,
  // September rollover wizard (Task 20)
  useRolloverPreviewMutation,
  useRolloverApplyMutation,
  useRolloverUndoMutation,
  useGetStudentClassesQuery,
  useGetEnrollmentHistoryQuery,
  useGetEnrichedStudentsQuery,
  useBulkEnrollStudentsMutation,
  useGetStudentsByParentQuery: useLocalGetStudentsByParentQuery,
  useGetStudentsQuery: useLocalGetStudentsQuery,
  useExportStudentsMutation,
  useGetLocationGradesQuery,
  useCreateStudentMutation,
  useListIntakeTemplatesQuery,
  useListStudentIntakeInvitationsQuery,
  useDeleteIntakeFormMutation,
  useUpdateStudentMutation,
  useGetStudentQuery,
  useMarkStudentLeftMutation,
  useReactivateStudentMutation,
  useScheduleStudentLeavingMutation,
  useDeleteStudentMutation,
  useGetStudentOnboardingQuery,
  useResendStudentOnboardingMutation,
  useBulkDeactivateStudentsMutation,
  useUpdateProfileMutation,
  useGetLocationsQuery: useLocalGetLocationsQuery,
  useGetLocationByIdQuery: useLocalGetLocationByIdQuery,
  // Forms
  useGetFormsQuery,
  useGetFormQuery,
  useLazyGetDefaultRegistrationTemplateQuery,
  useGetStudentRegistrationResponseQuery,
  useListStudentParentsQuery,
  useUpdateStudentParentContactMutation,
  useAddStudentParentMutation,
  useRemoveStudentParentMutation,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useSubmitFormResponseMutation,
  useGetUploadUrlMutation,
  // Form Invitations
  useGetMyInvitationsQuery,
  useGetInvitationCountQuery,
  useGetMyChildrenQuery,
  useGetMyChildCoparentsQuery,
  useInviteCoparentMutation,
  useDistributeFormMutation,
  useGetFormDistributionsQuery,
  useGetFormDistributionStatsQuery,
  useUpdateFormLocationStatusMutation,
  useCloseDistributionMutation,
  useGetStudentsPaginatedQuery,
  // Team — Platform level
  useGetSuperAdminsQuery,
  useAddSuperAdminMutation,
  useRemoveSuperAdminMutation,
  // Team — Organization level
  useGetOrgMembersQuery,
  useAddOrgMemberMutation,
  useRemoveOrgMemberMutation,
  // Team — Location level
  useGetLocationMembersQuery,
  useGetLocationStaffQuery,
  useAddLocationMemberMutation,
  useGetClassStaffQuery,
  useSetClassStaffMutation,
  useGetStaffClassesQuery,
  useUpdateLocationMemberRoleMutation,
  useRemoveLocationMemberMutation,
  // Invite Signup (public)
  useGetInvitePreviewQuery,
  useCompleteInviteSignupMutation,
  // Resend Invite
  useResendAdminInviteMutation,
  useResendOrgMemberInviteMutation,
  useResendLocationMemberInviteMutation,
  // Context
  useGetContextsQuery,
  useSwitchContextMutation,
  // Attendance v2
  useGetSessionForClassDateQuery,
  useLazyGetSessionForClassDateQuery,
  useGetSessionQuery,
  useListSessionsForLocationQuery,
  useListLiveSessionsQuery,
  useGetClassSessionsQuery,
  useStartSessionMutation,
  useEndSessionMutation,
  useSessionCheckInMutation,
  useSessionCheckOutMutation,
  useSessionMultiCheckOutMutation,
  useSessionVoidCheckInMutation,
  useListPickupContactsQuery,
  useListMyPickupContactsQuery,
  useAddMyPickupContactMutation,
  useUpdateMyPickupContactMutation,
  useDeleteMyPickupContactMutation,
  useGetMeTodayQuery,
  // Messaging
  useSendMessageMutation,
  useListSentMessagesQuery,
  useGetMessageAuditQuery,
  useWithdrawMessageMutation,
  useListMyMessagesQuery,
  useGetMyUnreadCountQuery,
  useMarkMessageReadMutation,
  useMarkAllReadMutation,
  // Two-way threads
  useStartThreadMutation,
  useReplyToMessageMutation,
  useListMessageRepliesQuery,
  usePostThreadMessageMutation,
  useGetThreadQuery,
  useListStudentThreadsQuery,
  useListLocationThreadsQuery,
  // Push notifications
  useLazyGetVapidKeyQuery,
  useSubscribePushMutation,
  useUnsubscribePushMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  // Observations
  useCreateObservationMutation,
  useGetObservationQuery,
  useUpdateObservationMutation,
  useWithdrawObservationMutation,
  useListSessionObservationsQuery,
  useListStudentObservationsQuery,
  useListMyObservationsQuery,
  useGetMyObservationsUnreadCountQuery,
  useMarkObservationReadMutation,
  useMarkAllObservationsReadMutation,
  // Moments (photo moments + parent feed)
  useCreateMomentMutation,
  useGetChildFeedQuery,
  useListChildMomentsQuery,
  // Incidents (parent-facing)
  useListMyChildIncidentsQuery,
  useGetMyIncidentQuery,
  useAcknowledgeIncidentMutation,
  // Incidents (staff-facing)
  useCreateIncidentMutation,
  useListLocationIncidentsQuery,
  useListStudentIncidentsQuery,
  useGetIncidentQuery,
  useAmendIncidentMutation,
  useTuslaReportedIncidentMutation,
  useSubmitIncidentMutation,
  useDiscardIncidentDraftMutation,
  useWithdrawIncidentMutation,
  useCloseIncidentMutation,
  useChangeIncidentSeverityMutation,
  useSetIncidentGroupIdMutation,
  useGetIncidentFieldsQuery,
  useUpdateIncidentFieldsMutation,
  // Fire Drills
  useStartFireDrillMutation,
  useGetFireDrillQuery,
  useUpdateFireDrillMutation,
  useTickFireDrillAttendeeMutation,
  useCompleteFireDrillMutation,
  useResolveFireDrillIssueMutation,
  useWithdrawFireDrillMutation,
  useListFireDrillsQuery,
  // Fees
  useListStudentFeesQuery,
  useCreateFeeMutation,
  useUpdateFeeMutation,
  useDeleteFeeMutation,
  // Cleaning Schedules
  useListCleaningAreasQuery,
  useCreateCleaningAreaMutation,
  useUpdateCleaningAreaMutation,
  useCreateCleaningCompletionMutation,
  useListCleaningCompletionsQuery,
  // Learning Goals (Aistéar)
  useListLearningGoalsQuery,
  useCreateLearningGoalMutation,
  useUpdateLearningGoalMutation,
  useGetStudentLearningGoalCoverageQuery,
  // Retention (Task 21 — clocks only)
  useGetRetentionScheduleQuery,
  useUpdateRetentionRuleMutation,
  useGetRetentionOverviewQuery,
  useSetStudentLegalHoldMutation,
  useClearStudentLegalHoldMutation,
  // Medication
  useCreateMedicationMutation,
  useConsentMedicationMutation,
  useRevokeMedicationMutation,
  useEndMedicationMutation,
  useListStudentMedicationsQuery,
  useListMyChildMedicationsQuery,
  useAdministerMedicationMutation,
  useSessionMedicationsDueQuery,
  useSessionSummaryQuery,
  useLocationMedicationsDueQuery,
  // Policy vault
  useGetLocationPoliciesQuery,
  useGetPolicyQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useSetPolicyPaperBasedMutation,
  useCreatePolicyVersionMutation,
  useRecordPolicyReviewMutation,
  useAcknowledgePolicyMutation,
  useGetMyPoliciesQuery,
  useListStudentPoliciesQuery,
  useGetPolicyCoverageQuery,
  useGetPolicySignaturesQuery,
  useExportPolicySignaturesMutation,
  useNudgePolicyMutation,
  // Daily Checklists
  useListChecklistItemsQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useListChecklistRunsQuery,
  useListUnsignedChecklistRunsQuery,
  useGetOrCreateChecklistRunMutation,
  useAnswerChecklistItemsMutation,
  useSignOffChecklistRunsMutation,
  // Staff Compliance (Garda Vetting & Credentials)
  useGetStaffComplianceQuery,
  useListStaffComplianceRegisterQuery,
  useUpsertVettingMutation,
  useUpsertCredentialMutation,
  useVerifyCredentialMutation,
  useUpsertReferenceMutation,
  useGetExpectedItemsQuery,
  useGetAttachmentUrlMutation,
} = localApi;
